import { AccessUseSchema } from '../models/access';
import { FragmentsViewSchema } from '../models/fragments';
import { HolderNameSchema } from '../models/holder-name';
import { GrimoireViewSchema } from '../models/grimoire';
import { ProgressSchema } from '../models/progress';
import { CoalitionViewSchema } from '../models/coalition';
import { PowerIdSchema, ReputationBandSchema } from '../models/reputation';
import { ContractViewSchema, NeededGiversSchema, QuestEntrySchema } from '../models/quest';
import { z } from 'zod';
import { ErrorCodeSchema } from '../errors';
import { PlayerIdSchema, SessionIdSchema } from '../ids';
import { BuildingIdSchema, LotIdSchema, OperationIdSchema } from '../ids';
import { CampStateSchema } from '../models/camp';
import {
  AttackViewSchema,
  CombatCatalogSchema,
  FamilyViewSchema,
  MAX_HAND_INDEX,
  WeaponFamilyIdSchema,
} from '../models/combat';
import { InventorySchema, ObjectIdSchema } from '../models/inventory';
import { ItemAmountsSchema, ItemIdSchema } from '../models/items';
import { NodeIdSchema, NodeStateSchema, PlantingStateSchema } from '../models/nodes';
import { SessionTokenSchema } from '../models/session';
import { StructureDefIdSchema } from '../models/town';
import { LotRaidSchema, LotStateSchema } from '../models/lot';
import { ToneStateSchema } from '../models/tone';
import { ReserveStateSchema, SurveyKnowledgeSchema } from '../models/survey';
import { TimestampMsSchema } from '../time';
import { RumorLineSchema } from '../http/contracts/rumors';

/**
 * Realtime protocol between client and server.
 *
 * Messages are discriminated by the short `t` field to keep frames small; snapshots sent many
 * times per second will dominate bandwidth, so field names stay terse in high-frequency
 * messages. Bump PROTOCOL_VERSION on any breaking change: the server rejects mismatches.
 */
export const PROTOCOL_VERSION = 144 as const;

/**
 * What a zone looks like (COMBAT-AOE; TECHNIQUE-BATCH-B, C), by index in its `zone` event: one that
 * strikes (a field, a landing, a staff's wave), one that gives its caster's side a state (a banner,
 * a circle), one that hides (a cloud of Niebla), one that frightens (a staked body, Estaca), and a
 * song's (FT-2, SO-4): `songRise` its warning (the note rising), `song` while it is sung, both
 * around its singer and both ended early by an `interrupt` of their `id` (the song was cut).
 */
export const ZONE_LOOKS = ['strike', 'allies', 'conceal', 'dread', 'songRise', 'song'] as const;
export type ZoneLook = (typeof ZONE_LOOKS)[number];

/**
 * Realtime timeline (simulation, snapshots, clock sync): epoch-aligned monotonic milliseconds
 * from the server, fractional. Never mixed with persisted wall-clock timestamps.
 */
export const NetTimeSchema = z.number().nonnegative();

/** Entity id within the simulation: small integers keep snapshots compact. */
export const EntityIdSchema = z.number().int().nonnegative();
export type EntityId = z.infer<typeof EntityIdSchema>;

export const WS_PATH = '/ws';

/** Close codes in the 4000-4999 range reserved for applications by RFC 6455. */
export const WsCloseCode = {
  Normal: 1000,
  ServerShutdown: 1001,
  ProtocolError: 4000,
  Unauthorized: 4001,
  SessionReplaced: 4002,
  SessionRevoked: 4003,
  AuthTimeout: 4008,
  RateLimited: 4029,
  /** Dev tool: simulated network drop. Not terminal, the client reconnects. */
  DevDrop: 4100,
} as const;
export type WsCloseCode = (typeof WsCloseCode)[keyof typeof WsCloseCode];

/** Close codes after which reconnecting with the same credentials cannot succeed. */
export const TERMINAL_CLOSE_CODES: ReadonlySet<number> = new Set([
  WsCloseCode.ProtocolError,
  WsCloseCode.Unauthorized,
  WsCloseCode.SessionReplaced,
  WsCloseCode.SessionRevoked,
]);

// ---------------------------------------------------------------- client -> server

/**
 * The 3D view's box (ADR 0074): what the server sends a client that drew the world in 3D, in
 * metres from its character, north being -y; it never turns with the camera.
 */
export const View3dSchema = z.object({
  north: z.number().positive(),
  south: z.number().positive(),
  halfWidth: z.number().positive(),
});
export type View3d = z.infer<typeof View3dSchema>;

export const HelloMessageSchema = z.object({
  t: z.literal('hello'),
  protocolVersion: z.number().int(),
  token: SessionTokenSchema,
  /**
   * The client draws the world in 3D (ADR 0074, B170's trial): the server sends it its 3D box
   * (`world.view3d`) instead of the window's view. Absent, the window's view, as always.
   */
  view: z.literal('3d').optional(),
});

export const PingMessageSchema = z.object({
  t: z.literal('ping'),
  /** Client clock when the ping was sent; echoed back to measure round-trip time. */
  clientTime: z.number(),
});

const AxisSchema = z.union([z.literal(-1), z.literal(0), z.literal(1)]);

/**
 * Movement intent for one simulation tick, sent every tick while a direction is held (plus one
 * "still" message on release). `seq` increases by one per message within a scene session; the
 * server acknowledges the last applied seq in snapshots so the client can reconcile.
 */
export const MoveMessageSchema = z.object({
  t: z.literal('move'),
  seq: z
    .number()
    .int()
    .nonnegative()
    .max(2 ** 31 - 1),
  dx: AxisSchema,
  dy: AxisSchema,
});

/** Client-chosen id echoed in the reply to a request (`scene` on success, `error` on failure). */
export const RequestRefSchema = z
  .number()
  .int()
  .nonnegative()
  .max(2 ** 31 - 1);

/**
 * Start gathering a resource node (PrimeraVersion.md §7). The character must stand still within
 * reach of an available node with room in the backpack. The server answers `gathering` (started)
 * then `gathered` (completed), or an `error` citing `req` (refused or interrupted). A node taken at
 * once (RES-GATHER RG-16: by hand, the catalog's `strikes.instant`) is answered with `gathered`
 * alone, walking or not. `op` makes the completion idempotent.
 */
export const GatherMessageSchema = z.object({
  t: z.literal('gather'),
  req: RequestRefSchema,
  op: OperationIdSchema,
  node: NodeIdSchema,
});

/**
 * One blow of the tool worn at a node within reach (INPUT-INTERACT, B147, IX-3): a click. The node
 * is gathered once its blows add up (`struck`, then `gathered`); `op` names the gathering, so every
 * blow of it carries the same. It swings toward `ax, ay` like an attack, and lands only if that
 * swing reaches the node (RES-GATHER RG-20); otherwise it is `missed`.
 */
export const StrikeMessageSchema = z.object({
  t: z.literal('strike'),
  req: RequestRefSchema,
  op: OperationIdSchema,
  node: NodeIdSchema,
  ax: z.number().min(-1_000).max(1_000),
  ay: z.number().min(-1_000).max(1_000),
});

/**
 * Plants a sapling of the backpack (`item`) in a stump within reach (RES-GATHER RG-13): a timed
 * action like gathering, with a shovel. The server answers `planting` then `planted`, or an
 * `error` citing `req`. `op` makes the completion idempotent.
 */
export const PlantMessageSchema = z.object({
  t: z.literal('plant'),
  req: RequestRefSchema,
  op: OperationIdSchema,
  node: NodeIdSchema,
  item: ItemIdSchema,
});

/**
 * Sows a sapling of the backpack (`item`) on a free world tile within reach (RES-GATHER RG-13 R2):
 * a timed action with a shovel, on land nobody holds or the planter's own lot (RF-1), where
 * `plantingRefusal` allows (PL-1, PL-2). The server answers `sowing` then `sown`, or an `error`
 * citing `req`.
 */
export const SowMessageSchema = z.object({
  t: z.literal('sow'),
  req: RequestRefSchema,
  op: OperationIdSchema,
  x: z.number().int(),
  y: z.number().int(),
  item: ItemIdSchema,
});

/**
 * Surveys what lies under the ground around the character (RES-DISTRIBUTION D3): a timed action
 * standing still with a prospector's kit (SD-5), anywhere (SD-9). The server answers `surveying`
 * then `surveyed`, or an `error` citing `req`.
 */
export const SurveyMessageSchema = z.object({
  t: z.literal('survey'),
  req: RequestRefSchema,
  op: OperationIdSchema,
});

/**
 * Works a deep vein below by hand (DEPTH-ECOLOGY DP-4, GK-8): one the character knows, named by the
 * mark it learned it by (never its id, OC-2), standing by it with a pick of its tier. A timed action
 * cut like gathering: answered `veinWorking` then `veinWorked`, or an `error` citing `req`.
 */
export const VeinMessageSchema = z.object({
  t: z.literal('vein'),
  req: RequestRefSchema,
  op: OperationIdSchema,
  vein: z.string().min(1).max(64),
});

/**
 * Probes the ground ahead for traps with a pole worn (SIEGE H2e, R-63): a timed action cut by
 * moving, answered `probing` then `probed` (the pole wore), or an `error` citing `req`. Done, its
 * prober alone sees every trap near where it probed (`traps`), none sprung.
 */
export const ProbeMessageSchema = z.object({
  t: z.literal('probe'),
  req: RequestRefSchema,
  op: OperationIdSchema,
});

/**
 * Pulls up a sapling not grown yet within reach (RES-GATHER PL-5): anyone's on land nobody holds,
 * on a lot only whoever may build on it. At once; the sapling is lost. Answered by `uprooted`.
 */
export const UprootMessageSchema = z.object({
  t: z.literal('uproot'),
  req: RequestRefSchema,
  op: OperationIdSchema,
  x: z.number().int(),
  y: z.number().int(),
});

/**
 * Works on a project within reach (BUILD technique 7, plan E2): a timed action like gathering
 * (moving, attacking or a blow cut it); anyone within reach may work (CB-3).
 */
export const WorkMessageSchema = z.object({
  t: z.literal('work'),
  req: RequestRefSchema,
  op: OperationIdSchema,
  project: BuildingIdSchema,
});

/**
 * Fells the tree on world tile (x, y) of the own lot (RV-27): a timed action like gathering logs,
 * whose wood it gives.
 */
/**
 * Goes down or up a Depth's mouth (DEPTH-ENTRANCE, DP-6): by either end within reach, a short
 * action cut like gathering; done, the character lands by the other end (`world` with `via`).
 */
export const DescendMessageSchema = z.object({
  t: z.literal('descend'),
  req: RequestRefSchema,
  op: OperationIdSchema,
});

export const FellMessageSchema = z.object({
  t: z.literal('fell'),
  req: RequestRefSchema,
  op: OperationIdSchema,
  x: z.number().int(),
  y: z.number().int(),
});

/**
 * Forces the lock of someone else's storage on a lot (HOME technique 11, plan C6): a timed action
 * like gathering, cut by moving, attacking or a blow; its owner hears an alarm.
 */
export const ForceMessageSchema = z.object({
  t: z.literal('force'),
  req: RequestRefSchema,
  op: OperationIdSchema,
  building: BuildingIdSchema,
});

/**
 * Forces the lock of someone else's room at an inn (HOME, RV-14, AR-1..6), standing by its door:
 * a timed action like forcing a storage's; the room is named by its number at that inn (AR-2:
 * never by whose it is). A sanctuary's rooms are never forced (`room_safe`).
 */
export const ForceRoomMessageSchema = z.object({
  t: z.literal('forceRoom'),
  req: RequestRefSchema,
  op: OperationIdSchema,
  inn: z.string().min(1).max(64),
  room: z.number().int().positive(),
});

/**
 * Wrecks a temporary camp in its region's Intense Pulse (plan C8, CM-3; H1's window): a timed
 * action like forcing a lock, cut by moving, attacking or a blow; its owner hears an alarm.
 */
export const WreckMessageSchema = z.object({
  t: z.literal('wreck'),
  req: RequestRefSchema,
  op: OperationIdSchema,
  building: BuildingIdSchema,
});

/**
 * A siege engine's shot at someone else's piece (SIEGE H2b2, RM-5): its shooter by the engine (a
 * catapult, a ballista on its own siege camp), the piece within its range and, a ballista's, along
 * a clear line; a timed action like a stroke, in the piece's damage window, one of its magazine's
 * ammunition spent. Answered like a stroke (`breaching`, `breached`).
 */
export const ShootMessageSchema = z.object({
  t: z.literal('shoot'),
  req: RequestRefSchema,
  op: OperationIdSchema,
  engine: BuildingIdSchema,
  building: BuildingIdSchema,
});

/**
 * Sets a gunpowder charge against someone else's piece (SIEGE, R-65, GP-1..4): by it, in its damage
 * window, a charge carried; a timed action cut like a stroke (`setting`), the charge spent when it
 * is set (`blastSet`); its fuse burns for anyone near to see (`fuse`) and blows like a stroke.
 */
export const BlastMessageSchema = z.object({
  t: z.literal('blast'),
  req: RequestRefSchema,
  op: OperationIdSchema,
  building: BuildingIdSchema,
});

/** Puts out a burning fuse by it (GP-1 a)): anyone, a timed action (`defusing`, `defused`). */
export const DefuseMessageSchema = z.object({
  t: z.literal('defuse'),
  req: RequestRefSchema,
  op: OperationIdSchema,
  fuse: z.string().min(1).max(64),
});

/**
 * A stroke against someone else's piece on a lot (SIEGE H2a, SG-1..3): a timed action like forcing
 * a lock (cut by moving, attacking or a blow), only in its site's damage window (a loose house's
 * Intense Pulse with its people connected, a small site's Major Pulse). Its owner hears an alarm.
 */
export const BreachMessageSchema = z.object({
  t: z.literal('breach'),
  req: RequestRefSchema,
  op: OperationIdSchema,
  building: BuildingIdSchema,
});

/**
 * A melee swing toward the cursor (PrimeraVersion.md §8; ADR 0013). `ax, ay` is the aim
 * direction in world units (normalized by the server); `at` is the client's remote render time
 * (server timeline) when the button was pressed: the server checks targets where the attacker
 * saw them, rewinding at most `combat.maxRewindMs`. Never a result: the server decides the hit.
 * Refusals answer an `error` citing `req`; accepted swings show up as snapshot events.
 */
export const AttackMessageSchema = z.object({
  t: z.literal('attack'),
  req: RequestRefSchema,
  ax: z.number().min(-1_000).max(1_000),
  ay: z.number().min(-1_000).max(1_000),
  at: NetTimeSchema,
  /**
   * The client's next input seq (COMBAT-DEFENSE): an attack lowers a raised guard or releases an
   * aim, and their slowing ends from this input, in the server and in the owner's prediction.
   */
  from: z.number().int().nonnegative().optional(),
  /**
   * When the client sent it, on the client's own monotonic clock (ms, any origin: never its
   * estimate of the server's, which drifts and jumps). The server learns how that clock sits
   * against its own and judges the weapon's recovery, the chain's window and a charge's hold at
   * that instant (bounded by `combat.attack.sentLagMs`), so the network's jitter never moves them.
   */
  sent: z.number().optional(),
});

/**
 * A dodge (COMBAT-DEFENSE): a burst of speed toward `dx, dy` (one of the 8 directions) for the
 * own inputs from `from` (the client's next seq), spending a dodge charge. The client forces that
 * direction on those inputs; the server only accepts inputs it has not received yet.
 */
export const DodgeMessageSchema = z.object({
  t: z.literal('dodge'),
  req: RequestRefSchema,
  dx: z.number().int().min(-1).max(1),
  dy: z.number().int().min(-1).max(1),
  from: z.number().int().nonnegative(),
});

/**
 * Starts charging the weapon's attack toward `ax, ay` (COMBAT-ATTACK), or turns the charge held:
 * its slowing rules the own inputs from `from`. Released by `attack`; at its top it goes off by
 * itself. Refused (an `error` citing `req`) as an attack would be.
 */
export const ChargeMessageSchema = z.object({
  t: z.literal('charge'),
  req: RequestRefSchema,
  ax: z.number().min(-1_000).max(1_000),
  ay: z.number().min(-1_000).max(1_000),
  from: z.number().int().nonnegative(),
  /** When the client sent it (see `attack.sent`): a charge's hold is measured between these. */
  sent: z.number().optional(),
  /** Only turns a charge being held: never starts one (a refused start stays refused). */
  turn: z.boolean().optional(),
});

/**
 * Where the own character faces (COMBAT-ATTACK): the cursor's direction as a byte angle, told
 * when it turns. Only drawn: no rule reads it, and movement never follows it (MOVE-CHARACTER).
 */
export const FaceMessageSchema = z.object({
  t: z.literal('face'),
  a: z.number().int().min(0).max(255),
});

/**
 * Challenges the character `id` to a duel (DEATH-DUEL), or accepts its challenge: two crossed
 * challenges start it. Answered by `duel` messages, or an `error` citing `req`.
 */
export const DuelChallengeMessageSchema = z.object({
  t: z.literal('duel'),
  req: RequestRefSchema,
  id: EntityIdSchema,
});

/**
 * The secondary action of the weapon worn (COMBAT-DEFENSE), held while its button is: raised
 * (`on`) toward `ax, ay`, again when the aim turns, and lowered. Its slowing rules the own inputs
 * from `from`.
 */
export const SecondaryMessageSchema = z.object({
  t: z.literal('secondary'),
  req: RequestRefSchema,
  on: z.boolean(),
  ax: z.number().min(-1_000).max(1_000),
  ay: z.number().min(-1_000).max(1_000),
  from: z.number().int().nonnegative(),
});

/**
 * Put the weapon away or take it in hand again (B167, SH-1..4): sheathed it stays in its slot, but
 * no blow, charge, guard nor technique goes. Answered only when refused (an `error` citing `req`);
 * whoever sees the character learns it from its `gear`.
 */
export const SheatheMessageSchema = z.object({
  t: z.literal('sheathe'),
  req: RequestRefSchema,
  on: z.boolean(),
});

/**
 * Boards a ship within reach, moored at a dock or touching the bank where the character stands
 * (MOVE-SHIP T4), taking its first free seat. Answered only when refused (an `error` citing `req`);
 * everyone near hears it as an `aboard` event.
 */
export const BoardMessageSchema = z.object({
  t: z.literal('board'),
  req: RequestRefSchema,
  ship: EntityIdSchema,
});

/** Goes ashore from the ship it is on, to the nearest walkable tile within reach (MOVE-SHIP T4). */
export const DisembarkMessageSchema = z.object({
  t: z.literal('disembark'),
  req: RequestRefSchema,
});

/**
 * Takes (`on`) or lets go of its ship's helm (MOVE-SHIP T3): at it, the character's `move` inputs
 * steer the ship, and its frames carry the ship's state (`snap.helm`).
 */
export const HelmMessageSchema = z.object({
  t: z.literal('helm'),
  req: RequestRefSchema,
  on: z.boolean(),
});

/**
 * Start looting the camp of the current scene (PrimeraVersion.md §11): a brief interaction,
 * completed by the server after `camp.loot.durationMs` unless the looter moves, attacks, is hit
 * or falls. Answered by `looting` (started) and `looted` (completed), or an `error` citing `req`.
 */
export const LootMessageSchema = z.object({
  t: z.literal('loot'),
  req: RequestRefSchema,
  op: OperationIdSchema,
});

/**
 * Loot a place's chest (CREATURE-AI, AI-4): a timed action within reach of it, cut like a
 * camp's loot (moving, attacking, being hit). The server answers `chestLooting` then
 * `chestLooted` (what it took), or an `error` citing `req`. Idempotent by `op`.
 */
export const LootChestMessageSchema = z.object({
  t: z.literal('lootChest'),
  req: RequestRefSchema,
  op: OperationIdSchema,
  place: z.string().max(64),
});

/**
 * Opens a fixed treasure (WORLD-TREASURE T1): within reach of it, one the character has not found;
 * a timed action that moving, attacking or a blow cuts. Answered `treasureOpening` when it starts
 * and `treasureOpened` when what it holds came to its finder.
 */
export const OpenTreasureMessageSchema = z.object({
  t: z.literal('openTreasure'),
  req: RequestRefSchema,
  op: OperationIdSchema,
  treasure: z.string().max(64),
});

/** Slots of a character's item bar (ITEM-CONSUME, R-11): keys 1–8. */
export const HOTBAR_SLOTS = 8;

/**
 * Use the item of the bar's `slot` (ITEM-CONSUME): the server resolves which item it holds and
 * starts using it from the backpack, in or out of combat. It answers `consuming` (started) then
 * `consumed`, or an `error` citing `req` (refused, or interrupted: a dodge, an attack, a
 * technique or a blow cut it; walking does not). `op` makes the consumption idempotent.
 */
export const UseMessageSchema = z.object({
  t: z.literal('use'),
  req: RequestRefSchema,
  op: OperationIdSchema,
  slot: z.number().int().min(1).max(HOTBAR_SLOTS),
});

/**
 * Pick up the objects on the ground within reach (ITEM-DROP): a brief interaction, interrupted
 * by moving, attacking or being hit (user decision EQ-3c). Idempotent.
 */
/** World positions as integers in 1/256 m (see @webgame/simulation POSITION_SCALE). */
export const FixedCoordinateSchema = z.number().int();

/**
 * A party action (SOC-PARTY): invite a character, accept or decline its invitation, leave the
 * party, or (its leader) take one out. `player` names whom it is about (not for `leave`).
 */
export const PartyActionMessageSchema = z.object({
  t: z.literal('party'),
  req: RequestRefSchema,
  do: z.enum(['invite', 'accept', 'decline', 'leave', 'kick', 'mark']),
  player: PlayerIdSchema.optional(),
  /** `mark`: the world point it shows its party on the map (null: takes its mark away). */
  at: z.tuple([FixedCoordinateSchema, FixedCoordinateSchema]).nullable().optional(),
});

export const PickupMessageSchema = z.object({
  t: z.literal('pickup'),
  req: RequestRefSchema,
  op: OperationIdSchema,
  drop: z.string().max(64),
});

/**
 * Lets something of the backpack fall where the character stands (B161), in combat too: one object
 * by id, or one unit of a good. It lies as the character's residue, anyone's to take.
 */
export const DiscardMessageSchema = z
  .object({
    t: z.literal('discard'),
    req: RequestRefSchema,
    op: OperationIdSchema,
    object: ObjectIdSchema.optional(),
    item: ItemIdSchema.optional(),
  })
  .refine((m) => (m.object === undefined) !== (m.item === undefined), {
    message: 'One object or one good',
  });

/**
 * Takes the parts of a creature's body within reach (RES-GATHER RG-4): a timed action like a
 * pickup, with the best tools the backpack holds; what no tool carried can take stays in it.
 */
export const HarvestMessageSchema = z.object({
  t: z.literal('harvest'),
  req: RequestRefSchema,
  op: OperationIdSchema,
  drop: z.string().max(64),
});

/**
 * Pays the toll a creature takes by its body (VB-1: the tollers of Senra): coins from the treasury
 * to its power's, for its safe conduct a while. Idempotent.
 */
export const TollMessageSchema = z.object({
  t: z.literal('toll'),
  req: RequestRefSchema,
  op: OperationIdSchema,
  collector: EntityIdSchema,
});

/**
 * Speaks to a wounded traveler waiting by the road (WORLD-ENCOUNTER EN-3, VJ-1): without `accept`
 * it tells where it is going (`escort` `offer`); with it, it follows the speaker there (`follow`).
 * Refused with `no_traveler` (none waiting within reach) or `already_escorting` (one at a time a
 * character or its party).
 */
export const EscortMessageSchema = z.object({
  t: z.literal('escort'),
  req: RequestRefSchema,
  traveler: EntityIdSchema,
  accept: z.boolean(),
});

/**
 * Wear an object of the backpack in its slot; the one worn there goes to the backpack
 * (EQUIP-SLOT). Out of combat only. Idempotent.
 */
export const EquipMessageSchema = z.object({
  t: z.literal('equip'),
  req: RequestRefSchema,
  op: OperationIdSchema,
  object: ObjectIdSchema,
});

/**
 * Put unspent points into attributes (CHAR-LEVEL): attribute id → points. Out of combat.
 * Idempotent.
 */
export const AllocateMessageSchema = z.object({
  t: z.literal('allocate'),
  req: RequestRefSchema,
  op: OperationIdSchema,
  points: z.record(z.string().max(32), z.number().int().positive().max(1000)),
});

/**
 * Choose a perk at an attribute's threshold (CHAR-ATTRIBUTE §6, AO-3): one of those offered there,
 * once the attribute reaches it, one per threshold. Out of combat. Idempotent. Answered like
 * `allocate`, with the progress.
 */
export const PerkMessageSchema = z.object({
  t: z.literal('perk'),
  req: RequestRefSchema,
  op: OperationIdSchema,
  attribute: z.string().max(32),
  threshold: z.number().int().positive().max(1000),
  perk: z.string().max(32),
});

/**
 * An action at the own station (MAGIC-STATION), out of combat: absorb or extract a fragment,
 * fuse fragments into `item` (MAGIC-FUSION), save or apply a loadout, or reassign attributes.
 * Idempotent.
 */
export const AttuneActionSchema = z.discriminatedUnion('k', [
  z.object({ k: z.literal('absorb'), item: ItemIdSchema }),
  z.object({ k: z.literal('extract'), item: ItemIdSchema }),
  z.object({ k: z.literal('fuse'), item: ItemIdSchema }),
  z.object({
    k: z.literal('saveLoadout'),
    name: z.string().min(1).max(24),
    fragments: z.array(ItemIdSchema).max(12),
  }),
  z.object({ k: z.literal('applyLoadout'), name: z.string().min(1).max(24) }),
  z.object({
    k: z.literal('respec'),
    points: z.record(z.string().max(32), z.number().int().nonnegative().max(1000)),
  }),
]);
export type AttuneAction = z.infer<typeof AttuneActionSchema>;

/**
 * Use the technique of an absorbed fragment (MAGIC-TECHNIQUE) toward `ax, ay` (a direction),
 * seen at `at` (view time, like `attack`). The shard spends its Focus and judges it; what it does
 * shows as snapshot events. `from` is the first own input a dash rules (the client's next seq).
 */
export const TechniqueMessageSchema = z.object({
  t: z.literal('technique'),
  req: RequestRefSchema,
  /**
   * The skill slot (WEAPON-TECHNIQUE): 1 and 2 the weapon's techniques (Q, E), 3 to 6 magic with
   * a wand or a staff (R, T, Z, X). The shard reads what sits there.
   */
  slot: z.number().int().min(1).max(6),
  ax: z.number(),
  ay: z.number(),
  at: NetTimeSchema,
  from: z.number().int().nonnegative(),
});

export const AttuneMessageSchema = z.object({
  t: z.literal('attune'),
  req: RequestRefSchema,
  op: OperationIdSchema,
  action: AttuneActionSchema,
});

/**
 * Learns `spell` from a master (MAGIC-GRIMOIRE MA-1..3): by it at its work, out of combat (the
 * shard), for its price and the standing its power asks (the host). Idempotent.
 */
export const LearnMessageSchema = z.object({
  t: z.literal('learn'),
  req: RequestRefSchema,
  op: OperationIdSchema,
  teacher: z
    .string()
    .regex(/^[a-z][a-z0-9_]*$/)
    .max(64),
  spell: z.string().max(32),
});

/** Take off what is worn in `slot`, into the backpack (EQUIP-SLOT). Out of combat. Idempotent. */
export const UnequipMessageSchema = z.object({
  t: z.literal('unequip'),
  req: RequestRefSchema,
  op: OperationIdSchema,
  slot: z.string().max(32),
});

/**
 * Fill the quiver from the backpack, or empty it into the backpack (ITEM-AMMO), by up to `n` of
 * `item`: a fill takes what the backpack has and the quiver's room allows. Out of combat.
 * Answered by `quivered`. Idempotent.
 */
export const QuiverMessageSchema = z.object({
  t: z.literal('quiver'),
  req: RequestRefSchema,
  op: OperationIdSchema,
  action: z.enum(['fill', 'empty']),
  item: ItemIdSchema,
  n: z.number().int().positive().max(1_000),
});

/**
 * Charges the wand worn with a fragment of the backpack (ADR 0057, WF-4): a timed action a blow
 * cuts, walking does not; answered `loading` when it starts and `loaded` when it commits.
 */
export const LoadMessageSchema = z.object({
  t: z.literal('load'),
  req: RequestRefSchema,
  op: OperationIdSchema,
  item: ItemIdSchema,
});

/**
 * Makes one piece by hand (B064, R-43, R-44): a recipe of `crafting.hand` from the backpack's
 * goods, out of combat, a timed action walking does not cut and combat does. Answered
 * `handcrafting` when it starts and `handcrafted` when it commits (its goods taken then).
 */
export const HandcraftMessageSchema = z.object({
  t: z.literal('handcraft'),
  req: RequestRefSchema,
  op: OperationIdSchema,
  recipe: ItemIdSchema,
});

/**
 * Casts the rod worn toward `ax, ay` (a world point, RES-FISH): water within `fishing.castRange`
 * on the caster's layer, with a clear line. Answered `fishing` when the line lands; the bite then
 * shows as a `bite` event, and `fished` settles it. Refusals answer an `error` citing `req`. `op`
 * makes the catch idempotent.
 */
export const FishMessageSchema = z.object({
  t: z.literal('fish'),
  req: RequestRefSchema,
  op: OperationIdSchema,
  ax: z.number().min(-1_000_000).max(1_000_000),
  ay: z.number().min(-1_000_000).max(1_000_000),
});

/**
 * Reels the line in (RES-FISH, PS-1): judged at `at`, the client's remote render time when the
 * button was pressed (bounded by `combat.maxRewindMs`, like an attack), so latency never eats the
 * bite's window. Inside it the fish is caught; before or after it, it gets away (no error). It
 * answers through the cast's `req`.
 */
/**
 * The chat's channels (SOC-CHAT): who hears a line. `near` within the voice's reach, `party` and
 * `company` their members, `region` everyone in the geographic region, `world` everyone, and
 * `whisper` one character by name.
 */
export const ChatChannelSchema = z.enum(['near', 'party', 'company', 'region', 'world', 'whisper']);
export type ChatChannel = z.infer<typeof ChatChannelSchema>;

/** The languages a line may say it is written in (the writer's interface, CH-7). */
export const ChatLanguageSchema = z.enum(['en', 'es']);

/** A character's name as players type it (a whisper's target). */
const CharacterNameSchema = z.string().min(1).max(32);

/**
 * What a player writes (SOC-CHAT): at most 200 characters, none of them control characters
 * (nothing may pass for a line of the system).
 */
const ChatTextSchema = z
  .string()
  .min(1)
  .max(200)
  .regex(/^\P{Cc}*$/u);

/**
 * Says a line in a channel (SOC-CHAT); `to` names a whisper's target. Answered only when refused
 * (citing `req`); its own line comes back like everyone's. `lang` is the writer's interface
 * language, for readers to filter by (CH-7); nothing is translated.
 */
export const SayMessageSchema = z.object({
  t: z.literal('say'),
  req: RequestRefSchema,
  ch: ChatChannelSchema,
  text: ChatTextSchema,
  to: CharacterNameSchema.optional(),
  lang: ChatLanguageSchema,
});

/**
 * Ignores a character, or stops (SOC-BLOCK): by its id (from a line) or its name (typed). Answered
 * with the own list (`chatIgnores`), or a refusal.
 */
export const ChatIgnoreMessageSchema = z.object({
  t: z.literal('chatIgnore'),
  req: RequestRefSchema,
  player: PlayerIdSchema.optional(),
  name: CharacterNameSchema.optional(),
  on: z.boolean(),
});

/** Reports a line the receiver heard (OPS-MODERATION): kept with what was said before it. */
export const ChatReportMessageSchema = z.object({
  t: z.literal('chatReport'),
  req: RequestRefSchema,
  id: z.number().int().nonnegative(),
  reason: z.string().max(200),
});

export const ReelMessageSchema = z.object({
  t: z.literal('reel'),
  req: RequestRefSchema,
  at: NetTimeSchema,
});

export const ClientMessageSchema = z.discriminatedUnion('t', [
  HelloMessageSchema,
  PartyActionMessageSchema,
  SayMessageSchema,
  ChatIgnoreMessageSchema,
  ChatReportMessageSchema,
  ChargeMessageSchema,
  FaceMessageSchema,
  DuelChallengeMessageSchema,
  PingMessageSchema,
  MoveMessageSchema,
  GatherMessageSchema,
  StrikeMessageSchema,
  PlantMessageSchema,
  SowMessageSchema,
  UprootMessageSchema,
  SurveyMessageSchema,
  VeinMessageSchema,
  ProbeMessageSchema,
  WorkMessageSchema,
  FellMessageSchema,
  DescendMessageSchema,
  ForceMessageSchema,
  ForceRoomMessageSchema,
  WreckMessageSchema,
  BreachMessageSchema,
  ShootMessageSchema,
  BlastMessageSchema,
  DefuseMessageSchema,
  UseMessageSchema,
  LootChestMessageSchema,
  OpenTreasureMessageSchema,
  AttackMessageSchema,
  LootMessageSchema,
  EquipMessageSchema,
  UnequipMessageSchema,
  AllocateMessageSchema,
  PerkMessageSchema,
  AttuneMessageSchema,
  LearnMessageSchema,
  TechniqueMessageSchema,
  DodgeMessageSchema,
  SecondaryMessageSchema,
  SheatheMessageSchema,
  BoardMessageSchema,
  DisembarkMessageSchema,
  HelmMessageSchema,
  PickupMessageSchema,
  DiscardMessageSchema,
  HarvestMessageSchema,
  TollMessageSchema,
  EscortMessageSchema,
  QuiverMessageSchema,
  LoadMessageSchema,
  HandcraftMessageSchema,
  FishMessageSchema,
  ReelMessageSchema,
]);
export type ClientMessage = z.infer<typeof ClientMessageSchema>;

/**
 * Every client message type, read from the schema: lists that route them derive from it, so a new
 * message never has to be remembered anywhere else.
 */
export const CLIENT_MESSAGE_TYPES: readonly ClientMessage['t'][] = ClientMessageSchema.options.map(
  (option) => option.shape.t.value,
);

// ---------------------------------------------------------------- server -> client

/** A catalog's hash: hex, from its content (NET-CLIENT-CACHE C2, `getCatalog`). */
export const CatalogHashSchema = z.string().regex(/^[0-9a-f]{16,64}$/);

export const WelcomeMessageSchema = z.object({
  t: z.literal('welcome'),
  /**
   * The server's build (ADR 0049): a client that already met another one in this page runs
   * older code than the server, and reloads itself.
   */
  build: z.string().min(1).max(64),
  /**
   * The hash of the server's catalog (NET-CLIENT-CACHE C2): a client that holds it asks nothing;
   * else it asks `getCatalog` for it.
   */
  catalog: CatalogHashSchema,
  sessionId: SessionIdSchema,
  playerId: PlayerIdSchema,
  serverTime: TimestampMsSchema,
  /** Authoritative simulation steps per second. */
  tickRate: z.number().int().positive(),
  /** State snapshots per second sent to clients in an active scene. */
  snapshotRate: z.number().int().positive(),
  /**
   * Rules the client needs to predict its own movement exactly like the server. Sent by the
   * server (not read from client defaults) because the server may run with tuned overrides.
   */
  movement: z.object({
    speedTilesPerSecond: z.number().positive(),
    bodyRadius: z.number().positive(),
  }),
  interpolationDelayMs: z.number().int().nonnegative(),
  interpolationIntervals: z.number().positive(),
  /** Reach for building interactions; the client shows prompts where the server accepts. */
  interactionRangeTiles: z.number().positive(),
  /**
   * What a player sees, in metres (ADR 0044, 0048): `height` high, as wide as the window's
   * aspect between `minAspect` and `maxAspect` times the height (bars beyond).
   */
  view: z.object({
    height: z.number().positive(),
    minAspect: z.number().positive(),
    maxAspect: z.number().positive(),
  }),
  /**
   * The box the server sends this client (ADR 0074): only to one that asked for the 3D view in its
   * hello, and only when the server has one; the 3D camera never frames more than it.
   */
  view3d: View3dSchema.optional(),
  /**
   * Heights as rules, in metres (ADR 0056): a layer's surface, a body's shot point, the ground's
   * walls, a deck's slab and a parapet's cover; a ground level's rise and the eyes' height
   * (WORLD-RELIEF). The client draws decks, relief and shots by them.
   */
  heights: z.object({
    layer: z.number().positive(),
    body: z.number().positive(),
    wall: z.number().positive(),
    slab: z.number().positive(),
    parapet: z.number().positive(),
    level: z.number().positive(),
    eye: z.number().positive(),
    /** The steepest rise per metre one walks (REL-5): the field's rock is derived by it (ADR 0078). */
    grade: z.number().positive(),
    /** The speed lost straight up the steepest walkable slope (REL-4): the client predicts by it. */
    climb: z.number().min(0),
    /** How far the ground under a footprint may fall (REL-7): the build mode previews by it. */
    plinth: z.number().min(0),
    /** How far a room's roof rises over its walls (WORLD-INTERIOR T13): the 3D view draws it. */
    roof: z.number().min(0),
    /** How high a blow and a shot reach (CREATURE-FLIER §2.3). */
    reach: z.number().positive(),
    shotCeiling: z.number().positive(),
    /** How far above or below its centre's ground an area reaches (REL-8): to draw it by. */
    zone: z.number().positive(),
  }),
  /** How Focus comes back (MAGIC-FOCUS), for the client to predict its own reserve. */
  focus: z.object({
    inCombatPeriodMs: z.number().int().positive(),
    outOfCombatPeriodMs: z.number().int().positive(),
  }),
  /**
   * Combat rules the client shows and paces locally (swing reach, cooldown). The server still
   * decides every hit.
   */
  combat: z.object({
    outOfCombatMs: z.number().int().nonnegative(),
    /** How long after its last combat act a character still faces its cursor (AN-3, AN-4). */
    engagedMs: z.number().int().nonnegative(),
    maxHealth: z.number().int().positive(),
    /** Attacks and weapons (COMBAT-ATTACK): to pace the own attacks and draw anyone's. */
    catalog: CombatCatalogSchema,
    /**
     * How a creature's blow comes on (CF-1): it walks its heading at `advancePace` of its speed
     * through the warning, closing at most `closeShare` of the gap, and lunges `lunge` metres as it
     * lands; for the client to draw where the blow falls. The server still decides every hit.
     */
    creatureStrike: z.object({
      advancePace: z.number().nonnegative(),
      lunge: z.number().nonnegative(),
      closeShare: z.number().min(0).max(1),
    }),
    /** The dodge's phases (B045), for the owner to predict it. */
    dodge: z.object({
      distance: z.number().positive(),
      startupMs: z.number().int().nonnegative(),
      invulnerableUntilMs: z.number().int().nonnegative(),
      tailUntilMs: z.number().int().nonnegative(),
      recoveryUntilMs: z.number().int().nonnegative(),
    }),
    /** Stamina (B049): how it comes back and what the own actions cost, to predict it. */
    stamina: z.object({
      maximum: z.number().positive(),
      regenPerS: z.number().positive(),
      delayMs: z.number().int().nonnegative(),
      recoverFraction: z.number().min(0).max(1),
      gaspMs: z.number().int().nonnegative(),
      attackCost: z.number().nonnegative(),
      dodgeCost: z.number().nonnegative(),
      guardCost: z.number().nonnegative(),
      chargeCost: z.number().nonnegative(),
    }),
    /**
     * Duels (DEATH-DUEL): whom a challenge reaches, how long it stands, and whether both must
     * stand in a sanctuary (B150).
     */
    duel: z.object({
      challengeRange: z.number().positive(),
      offerMs: z.number().int().positive(),
      sanctuaryOnly: z.boolean().default(true),
    }),
  }),
  /**
   * Planting (RES-GATHER RG-13): which goods are saplings and of what tree species, in definition
   * order, for the client to offer the right one at a stump. The server still judges the planting.
   */
  planting: z.object({
    saplings: z.array(z.tuple([ItemIdSchema, z.string().min(1).max(32)])),
    /** Where a sapling goes on free land (PL-1, PL-2), for `plantingRefusal`'s preview. */
    ground: z.object({ spacingM: z.number().positive(), roadMarginM: z.number().nonnegative() }),
  }),
  /** Hunting tracks (RES-GATHER CA-2): how long a fresh print lasts, for drawing it as it fades. */
  tracking: z.object({ freshMs: z.number().int().positive() }),
  /**
   * How worn things work (ITEM-WEAR §3, RES-GATHER RG-9): whole above `fullAbove` of their maximum
   * (the notice's mark), down to `floor` at 0; for `wearFactor` on the client, never fixed there.
   * A fighting piece does its factor in steps of `combatStep` (ITEM-WEAR §9, `combatWearFactor`).
   */
  wear: z.object({
    fullAbove: z.number().min(0).max(1),
    floor: z.number().min(0).max(1),
    combatStep: z.number().positive().max(0.5),
  }),
});

export const PongMessageSchema = z.object({
  t: z.literal('pong'),
  clientTime: z.number(),
  serverTime: NetTimeSchema,
});

export const ErrorMessageSchema = z.object({
  t: z.literal('error'),
  code: ErrorCodeSchema,
  message: z.string(),
  /** The request that failed, when the error answers one (e.g. `gather`). */
  req: RequestRefSchema.optional(),
  /** How long until it may be tried again (ms), when that is known (a silence, a pace). */
  retryMs: z.number().int().nonnegative().optional(),
});

/** Vertical layer of a position (ADR 0035): 0 is the ground, upper layers are structures. */
export const LayerSchema = z.number().int().min(0).max(3);

/**
 * A scene entity: a player's character or an enemy. Enemies have no player; their `name` is
 * their kind (clients translate it). Health changes travel as snapshot combat events.
 */
/** An index in the combat catalog's `hands` (B115): what a hand holds, as clients draw it. */
const HandIndexSchema = z.number().int().min(0).max(MAX_HAND_INDEX);
/** What a character holds in each hand (B115); absent: nothing there. */
export const HandsSchema = z.object({
  main: HandIndexSchema.optional(),
  off: HandIndexSchema.optional(),
  /**
   * Its weapon put away (B167): what `main` names is in its sheath, and an `off` that is no light
   * with it; nothing it wields strikes nor guards.
   */
  sh: z.literal(true).optional(),
});
export type Hands = z.infer<typeof HandsSchema>;

/** The most marks an entity's description and a `marks` event carry (CHAR-MARK). */
export const MAX_MARKS = 4;

export const EntityInfoSchema = z.object({
  id: EntityIdSchema,
  kind: z.enum(['player', 'enemy', 'ship']),
  playerId: PlayerIdSchema.nullable(),
  name: z.string(),
  isBot: z.boolean(),
  x: FixedCoordinateSchema,
  y: FixedCoordinateSchema,
  layer: LayerSchema,
  hp: z.number().int().nonnegative(),
  maxHp: z.number().int().positive(),
  dead: z.boolean(),
  /** The angle (byte) of its raised guard, when it has one up (COMBAT-DEFENSE). */
  guard: z.number().int().min(0).max(255).optional(),
  /** Where it faces (byte angle, COMBAT-ATTACK), once its client told. */
  face: z.number().int().min(0).max(255).optional(),
  /** What it holds in its hands (B115), by index in the catalog's `hands`: drawn only. */
  hands: HandsSchema.optional(),
  /** Its level (CHAR-POWER): its colour for whoever meets it (PO-9), the gap of its blows (PO-7). */
  lv: z.number().int().min(1).max(255).optional(),
  /** Its states (COMBAT-STATUS): index in the catalog and milliseconds left, for who meets it. */
  states: z
    .array(z.tuple([z.number().int().min(0).max(255), z.number().int().nonnegative()]))
    .optional(),
  /**
   * A named elite's name (CREATURE-SPAWN SP-3): its index among its species' titles
   * (`creatureTitles.<species>.<n>`, the species being its name unless it leads another's group).
   */
  title: z.number().int().min(0).max(63).optional(),
  /** The species whose titles name it, when it is not its own sheet (the herd's stag). */
  species: z.string().max(64).optional(),
  /** Its step of neglect (IE-3): nobody hunted its kind there for a while, and it grew (1-10). */
  aged: z.number().int().min(1).max(10).optional(),
  /**
   * It flies (CREATURE-FLIER): how high over its layer, in decimetres, drawn lifted over its shadow.
   * It comes down to its `dive` height for the recovery of each of its blows (its swing tells when).
   */
  hover: z.number().int().min(1).max(140).optional(),
  dive: z.number().int().min(0).max(140).optional(),
  /** A boss (CREATURE-BOSS): its bar and name are shown to whoever meets it, with its phase. */
  boss: z.literal(true).optional(),
  phase: z.number().int().min(1).max(8).optional(),
  /** A character's marks (CHAR-MARK, VM-2), by index in the catalog's `marks`: seen beside its name. */
  marks: z.array(z.number().int().min(0).max(63)).max(MAX_MARKS).optional(),
  /**
   * A ship (MOVE-SHIP T1): its class (`ships.classes`), heading (byte, a whole turn = 256, east at
   * 0, clockwise) and its id in storage (`row`: what its hold and its owner's requests name); its
   * health is its hull's integrity, and turns travel as `turn` events.
   */
  ship: z
    .object({
      c: z.string().min(1).max(32),
      h: z.number().int().min(0).max(255),
      row: z.string().min(1).max(64),
    })
    .optional(),
  /**
   * Its name when it holds a role (CREATURE-NAMED): `n` never translated (R5); shown with its role's
   * words (`roles.<r>.m` or `.f` when `f`), its byname `b` of its pool `p` (`bynames.<p>.<b>`), and a
   * dynasty's ordinal `o` (NN-1 c). Absent: its sheet's name.
   */
  nm: HolderNameSchema.optional(),
});
export type EntityInfo = z.infer<typeof EntityInfoSchema>;

/**
 * The receiver entered the world (on join, after every reconnection and after a respawn): its
 * own entity at its true position (it starts predicting from there) and the entities in its
 * area of interest. What surrounds it (plots, nodes, camps) arrives as `chunks`.
 */
/**
 * The fastest god mode (B131, the dev tools' only): its speed multiplies the character's pace. A
 * dev tool's bound, never a rule of play.
 */
export const GOD_MAX_SPEED = 8;

/**
 * A body's pace: its load's share of its speed times its Dexterity's (MOVE-CHARACTER,
 * CHAR-LEVEL). Above 1 only by Dexterity, which the config keeps small.
 */
const PaceSchema = z.number().positive().max(2);

export const WorldMessageSchema = z.object({
  t: z.literal('world'),
  /** Entity controlled by the receiving client. */
  selfId: EntityIdSchema,
  /** Its pace (its load slows it, MOVE-CHARACTER), from the first input of the stream. */
  pace: PaceSchema,
  /** Whether it may swim the deep water from the first input of the stream (MOVE-SWIM T3). */
  swims: z.boolean(),
  /** God mode (B131, dev tools only): its speed from the first input of the stream; absent, off. */
  god: z
    .object({
      speed: z.number().int().min(1).max(GOD_MAX_SPEED),
      /** Its blows deal 9999 (B131); absent, as usual. */
      fullDamage: z.literal(true).optional(),
      /** Nothing costs it stamina (B131); absent, as usual. */
      freeStamina: z.literal(true).optional(),
      /** Nothing is hidden from it (B131); absent, as usual. */
      seeAll: z.literal(true).optional(),
      /** Creatures still perceive it (B131, a scenario watched); absent, nothing does. */
      perceived: z.literal(true).optional(),
    })
    .optional(),
  tick: z.number().int().nonnegative(),
  time: NetTimeSchema,
  ents: z.array(EntityInfoSchema),
  /** Why it starts over somewhere else: by a Depth's mouth (DEPTH-ENTRANCE), the client fades. */
  via: z.literal('descent').optional(),
});

/** How a creature's body gives its parts (RES-GATHER RG-4): what its family says, for the UI. */
export const HARVEST_KINDS = ['skin', 'dismantle'] as const;
/**
 * The prints a beast leaves (RES-GATHER CA-2, RA-5), for the client to draw: on the wire by index
 * in this list (`tracks` messages).
 */
export const TRACK_KINDS = ['paw', 'hoof', 'claw'] as const;
export type TrackKind = (typeof TRACK_KINDS)[number];
export type HarvestKind = (typeof HARVEST_KINDS)[number];

/**
 * What lies on the ground (ITEM-DROP, DEATH-LOOT): what fell where a character died, until
 * someone picks it up or it expires (`expiresAt`, game time): its objects (`items`, one id per
 * object) and its goods. Positions in metres, with their layer.
 */
export const DropStateSchema = z.object({
  id: z.string().max(64),
  x: z.number(),
  y: z.number(),
  layer: z.number().int().min(0).max(15),
  items: z.array(ItemIdSchema),
  goods: ItemAmountsSchema,
  expiresAt: TimestampMsSchema,
  /**
   * A creature's body (RES-GATHER RG-4): the parts it still holds, taken with their tools
   * (`harvest`), apart from what lies loose. Absent on a drop that is no body.
   */
  parts: ItemAmountsSchema.optional(),
  /** How its parts come off, by its family (with `parts`): a beast is skinned, bones dismantled. */
  harvest: z.enum(HARVEST_KINDS).optional(),
  /** Whose body it is (with `parts`): its creature sheet, named like a living one (SU-3). */
  sheet: z.string().min(1).max(64).optional(),
  /** The tool kinds its parts left still ask (with `parts`; `partTools`): its label names them. */
  asks: z
    .array(z.string().regex(/^[a-z][a-z0-9_]{0,31}$/))
    .max(8)
    .optional(),
  /**
   * A power's caravan (WORLD-ENCOUNTER, EN-2): its cargo, drawn as its cart. Taking from it is a
   * crime with that power where its people see it.
   */
  caravan: z.object({ power: z.string().min(1).max(64) }).optional(),
  /** A death's residue (DEATH-PULSE §3): the name of whoever fell, «the residue of Aria». */
  of: z.string().min(1).max(64).optional(),
  /** A sunk ship's wreck (MOVE-SHIP T9, NA-5 a): it lies on the bed, its hold and its crew's packs. */
  wreck: z.literal(true).optional(),
  /** A wreck's ship (its `row`): its owner refloats it from here while it lies (CW-10 a). */
  ship: z.string().min(1).max(64).optional(),
  /**
   * What did not fit from a gathering (RES-GATHER RG-18): taken at once within the hand's reach,
   * only by its gatherer until `until` (game time), then by anyone. Whose it is never travels.
   */
  spill: z.object({ until: TimestampMsSchema }).optional(),
});

/**
 * What a death costs depends on its context (DEATH-STATE): who dealt the killing blow; `spared`,
 * the lore campaign's first death that costs nothing (KM-4), chosen by the host, never by a shard;
 * `depth`, any death inside a Depth (DEPTH-DEATH), chosen by where the body falls.
 */
export const DEATH_CONTEXTS = ['pve', 'pvp', 'spared', 'depth'] as const;
export type DeathContext = (typeof DEATH_CONTEXTS)[number];
export type DropState = z.infer<typeof DropStateSchema>;

/**
 * A place's chest (CREATURE-AI, AI-4), where its creatures keep what they loot: where it stands
 * and whether it holds anything (never what).
 */
export const ChestStateSchema = z.object({
  place: z.string().max(64),
  x: z.number(),
  y: z.number(),
  layer: z.number().int().min(0).max(255),
  filled: z.boolean(),
});
export type ChestState = z.infer<typeof ChestStateSchema>;

/** What a chunk holds besides terrain (which clients compile themselves, ADR 0032). */
/**
 * A worker at its post (PROD-WORKER technique 7): where it stands (world metres), its trade (what
 * it is seen doing) and its seed (its name and look, `workerName`). Shown only, never a body that
 * blocks or is struck (technique 8, until the sieges).
 */
export const WorkerBodySchema = z.object({
  id: z.string().min(1).max(64),
  x: z.number(),
  y: z.number(),
  trade: z.string().min(1).max(32),
  seed: z.number().int(),
  /**
   * A ship's hand (MOVE-SHIP CW-2): seated aboard, drawn at its seat as its ship moves (`x`, `y` only
   * tell the chunk); absent for a worker standing at its post.
   */
  aboard: z.object({ ship: EntityIdSchema, seat: z.number().int().min(1).max(20) }).optional(),
});
export type WorkerBody = z.infer<typeof WorkerBodySchema>;

export const ChunkStateSchema = z.object({
  cx: z.number().int(),
  cy: z.number().int(),
  /** Lots that overlap the chunk (HOME-CLAIM; repeated in every chunk they overlap). */
  lots: z.array(LotStateSchema),
  /** Nodes of the chunk that are depleted now; every other node is available. */
  depleted: z.array(NodeStateSchema),
  /** Trees planted in the chunk (RES-GATHER RG-13): laid on the map like the generator's. */
  plantings: z.array(PlantingStateSchema),
  /** Camps of the chunk, and whether each one's guard stands. */
  camps: z.array(z.object({ camp: CampStateSchema, defended: z.boolean() })),
  /** Objects on the ground in the chunk. */
  drops: z.array(DropStateSchema),
  /** The chests of the places in the chunk (AI-4), closed: only whether they hold something. */
  chests: z.array(ChestStateSchema),
  /** The workers standing at their posts in the chunk (PROD-WORKER F5). */
  workers: z.array(WorkerBodySchema),
});
export type ChunkState = z.infer<typeof ChunkStateSchema>;

/**
 * Chunk subscription: the state of chunks that came near the receiver, and those it no longer
 * needs. While a chunk is subscribed, the receiver gets `town`, `nodes` and `camp` changes in
 * it; nothing about the rest of the world.
 */
export const ChunksMessageSchema = z.object({
  t: z.literal('chunks'),
  enter: z.array(ChunkStateSchema),
  leave: z.array(z.tuple([z.number().int(), z.number().int()])),
});

/**
 * Interest management (ADR 0025): entities entering the receiver's area of interest (at the
 * position every receiver last got for them, so later deltas apply) and ids leaving it. A
 * receiver only gets snapshots, events and this message about entities near it. An id is always
 * left before it is reused for another entity.
 */
export const InterestMessageSchema = z.object({
  t: z.literal('interest'),
  enter: z.array(EntityInfoSchema),
  leave: z.array(EntityIdSchema),
});

/** Compact per-entity state: [id, x, y] with fixed-point coordinates. */
export const EntityStateSchema = z.tuple([
  EntityIdSchema,
  FixedCoordinateSchema,
  FixedCoordinateSchema,
]);
export type EntityState = z.infer<typeof EntityStateSchema>;

/** The controls a `control` event names, by index (COMBAT-CC). */
/**
 * What `control` events name by index. Slows and sunders are states now (COMBAT-STATUS, `state`
 * events): their indices stay unused so the others keep theirs.
 */
export const CONTROL_KINDS = ['slow', 'stun', 'knockback', 'sunder'] as const;

/**
 * Events of one tick (ADR 0022), carried by that tick's snapshot frame: combat, and the changes
 * of layer of entities (ADR 0035), which belong to the position of that same tick.
 */
export const CombatEventSchema = z.discriminatedUnion('k', [
  /**
   * An entity swung (players) or struck (enemies) toward `angle` (byte, full turn = 256) with
   * the attack of index `a` in the catalog (COMBAT-ATTACK), whose shape clients draw.
   */
  z.object({
    k: z.literal('swing'),
    id: EntityIdSchema,
    angle: z.number().int().min(0).max(255),
    a: z.number().int().min(0).max(255),
  }),
  z.object({
    k: z.literal('hit'),
    by: EntityIdSchema,
    target: EntityIdSchema,
    damage: z.number().int().nonnegative(),
    /** Target's health after the hit. */
    hp: z.number().int().nonnegative(),
    /**
     * The attack that struck (its index in the catalog, COMBAT-ATTACK), absent when nothing swung
     * (a trap, a state): how heavy it lands for the viewer (its hitstop, Docs/gamefeel).
     */
    a: z.number().int().min(0).max(254).optional(),
  }),
  z.object({ k: z.literal('death'), id: EntityIdSchema, by: EntityIdSchema.nullable() }),
  z.object({
    k: z.literal('heal'),
    id: EntityIdSchema,
    amount: z.number().int().nonnegative(),
    hp: z.number().int().nonnegative(),
  }),
  /** An entity telegraphs the attack of index `a` toward `angle`; it lands after `ms`. */
  z.object({
    k: z.literal('windup'),
    id: EntityIdSchema,
    angle: z.number().int().min(0).max(255),
    ms: z.number().int().nonnegative(),
    a: z.number().int().min(0).max(255),
  }),
  /** An entity moved to another layer (a ramp took it onto a deck or back to the ground). */
  z.object({ k: z.literal('layer'), id: EntityIdSchema, layer: LayerSchema }),
  /** A ship turned to heading `h` (byte; MOVE-SHIP T1). */
  z.object({ k: z.literal('turn'), id: EntityIdSchema, h: z.number().int().min(0).max(255) }),
  /**
   * `id` boarded `ship` at seat `seat` (0 is the helm) or, with `ship` null, went ashore (MOVE-SHIP
   * T4): aboard, it moves with its ship.
   */
  z.object({
    k: z.literal('aboard'),
    id: EntityIdSchema,
    ship: EntityIdSchema.nullable(),
    seat: z.number().int().min(0).max(255),
  }),
  /**
   * An entity stopped at `x`, `y` (fixed point, absolute). Snapshots do not carry every entity
   * in every frame (interest levels, NET-INTEREST), so a missing entity keeps moving on its
   * course; this says it stands still. It travels in the next snapshot, like every event.
   */
  z.object({ k: z.literal('rest'), id: EntityIdSchema, x: z.number().int(), y: z.number().int() }),
  /**
   * A barrier rises on a character (MAGIC-TECHNIQUE): it absorbs up to `absorb` damage for `ms`;
   * `absorb` 0 says it fell before its time.
   */
  z.object({
    k: z.literal('barrier'),
    id: EntityIdSchema,
    absorb: z.number().int().nonnegative(),
    ms: z.number().int().nonnegative(),
  }),
  /**
   * A projectile left `x`, `y` (fixed point, absolute) toward `angle` (full turn = 65536), cast
   * by `id` (MAGIC-TECHNIQUE). It flies straight at `speed` (cm/s), so clients draw its course
   * until its `boltEnd`; one per caster at a time. Events about it travel with the cell where
   * it is.
   */
  z.object({
    k: z.literal('bolt'),
    id: EntityIdSchema,
    angle: z.number().int().min(0).max(65535),
    x: z.number().int(),
    y: z.number().int(),
    speed: z.number().int().nonnegative(),
    /** Its look: an index of `PROJECTILE_LOOKS`. */
    look: z.number().int().min(0).max(255),
  }),
  /**
   * The projectile of `id` stopped at `x`, `y` (a body, the map, protected land or its range);
   * `r` is the radius of its burst in tenths of a metre (0: none, MAGIC-FUSION); `l` the layer it
   * ended on (ADR 0056: the body's it hit, else its flight's), so clients draw it rise or fall.
   */
  z.object({
    k: z.literal('boltEnd'),
    id: EntityIdSchema,
    x: z.number().int(),
    y: z.number().int(),
    r: z.number().int().min(0).max(255),
    l: z.number().int().min(0).max(255),
  }),
  /** A character raised (`on`) or lowered its guard toward `angle` (byte) (COMBAT-DEFENSE). */
  z.object({
    k: z.literal('guard'),
    id: EntityIdSchema,
    on: z.boolean(),
    angle: z.number().int().min(0).max(255),
    /** The guard's arc in degrees, as its weapon has it; 0 when unknown (drawn widest). */
    arc: z.number().int().min(0).max(360),
  }),
  /**
   * `id` parried a blow of `by` as its guard rose (COMBAT-DEFENSE): nothing got through, and `by`
   * recovers `ms` later (the parrier's weapon decides it; 0 when nothing was imposed).
   */
  z.object({
    k: z.literal('parry'),
    id: EntityIdSchema,
    by: EntityIdSchema,
    ms: z.number().int().min(0).max(65_535),
  }),
  /**
   * `id` placed a zone (COMBAT-AOE) at `x`, `y` (fixed point, absolute), `r` tenths of a metre
   * wide, for `ms`: clients draw it on the ground; its strikes come as `hit` and `control`. Its
   * `look` is an index of `ZONE_LOOKS` (TECHNIQUE-BATCH-C: a cloud of Niebla is no field).
   */
  z.object({
    k: z.literal('zone'),
    id: EntityIdSchema,
    x: z.number().int(),
    y: z.number().int(),
    r: z.number().int().min(0).max(255),
    ms: z.number().int().nonnegative(),
    look: z.number().int().min(0).max(255),
  }),
  /**
   * `id` holds a channel on `to` for `ms` more (TECHNIQUE-BATCH-C, the Drenaje's piece): clients
   * draw the thread between them; `ms` 0 says it ended (let go, cut, or its time).
   */
  z.object({
    k: z.literal('link'),
    id: EntityIdSchema,
    to: EntityIdSchema,
    ms: z.number().int().min(0).max(65_535),
  }),
  /**
   * `id` let a gust go (TECHNIQUE-BATCH-C, Vendaval): a cone toward `angle` (a byte, like a
   * swing's), `r` tenths of a metre long and `arc` degrees wide. The shots it turned come as new
   * `bolt` courses of their shooters.
   */
  z.object({
    k: z.literal('gust'),
    id: EntityIdSchema,
    angle: z.number().int().min(0).max(255),
    r: z.number().int().min(0).max(255),
    arc: z.number().int().min(0).max(360),
  }),
  /**
   * `id` carries state `s` (its index in the catalog, COMBAT-STATUS) for `ms` more: put on or
   * renewed.
   */
  z.object({
    k: z.literal('state'),
    id: EntityIdSchema,
    s: z.number().int().min(0).max(255),
    ms: z.number().int().nonnegative(),
  }),
  /** State `s` of `id` ended before its time (a cross rule, a duel's end). */
  z.object({
    k: z.literal('stateEnd'),
    id: EntityIdSchema,
    s: z.number().int().min(0).max(255),
  }),
  /**
   * `id` started charging attack `a` (index, COMBAT-ATTACK) toward `angle` (byte): the drawn
   * weapon shows, a warning that grows until the blow.
   */
  z.object({
    k: z.literal('charging'),
    id: EntityIdSchema,
    a: z.number().int().min(0).max(255),
    angle: z.number().int().min(0).max(255),
  }),
  /** A blow of `by` found `id` in its dodge's invulnerable phase (B045): it did nothing. */
  z.object({ k: z.literal('evade'), id: EntityIdSchema, by: EntityIdSchema }),
  /** `id` now faces `a` (byte angle, COMBAT-ATTACK): only drawn. */
  z.object({
    k: z.literal('face'),
    id: EntityIdSchema,
    a: z.number().int().min(0).max(255),
  }),
  /**
   * What `id` holds in its hands changed (B115: equipping, a two-handed weapon freeing the other
   * hand), by index in the catalog's `hands`: drawn only, never per frame.
   */
  z.object({
    k: z.literal('gear'),
    id: EntityIdSchema,
    main: HandIndexSchema.optional(),
    off: HandIndexSchema.optional(),
    /** Its weapon put away (B167, `Hands.sh`). */
    sh: z.literal(true).optional(),
  }),
  /** `id` spent Focus (COMBAT-TARGET): rivals see it, never how much. */
  z.object({ k: z.literal('cast'), id: EntityIdSchema }),
  /**
   * `id`'s marks changed (CHAR-MARK, VM-2), by index in the catalog's `marks`: whoever sees it
   * draws them beside its name; whoever meets it later reads its description.
   */
  z.object({
    k: z.literal('marks'),
    id: EntityIdSchema,
    marks: z.array(z.number().int().min(0).max(63)).max(MAX_MARKS),
  }),
  /**
   * A trait with its own signal on `id` (CHAR-TRAIT, TX-5: only a critical kept for its next blow,
   * PK-5): kept for `ms` (viewers mark it while it lasts), or 0 when a blow takes it. Never which
   * perk.
   */
  z.object({ k: z.literal('trait'), id: EntityIdSchema, ms: z.number().int().nonnegative() }),
  /** No player can hurt `id` for `ms` (SAFE-SPAWNCAMP); 0: its protection ended. */
  z.object({ k: z.literal('safe'), id: EntityIdSchema, ms: z.number().int().nonnegative() }),
  /** The wind-up `id` announced will not land (COMBAT-IMPACT): a blow interrupted it. */
  z.object({ k: z.literal('interrupt'), id: EntityIdSchema }),
  /** `id` staggers for `ms` (COMBAT-IMPACT): it cannot act, and walks slowly. */
  z.object({ k: z.literal('stagger'), id: EntityIdSchema, ms: z.number().int().nonnegative() }),
  /**
   * `id` suffers a control (COMBAT-CC) for `ms`: an index of `CONTROL_KINDS` (slowed, stunned,
   * knocked back), for viewers to show it.
   */
  z.object({
    k: z.literal('control'),
    id: EntityIdSchema,
    kind: z.number().int().min(0).max(255),
    ms: z.number().int().nonnegative(),
  }),
  /** A character's maximum health changed (CHAR-LEVEL: its Constitution), with its health. */
  z.object({
    k: z.literal('vitals'),
    id: EntityIdSchema,
    hp: z.number().int().nonnegative(),
    maxHp: z.number().int().positive(),
  }),
  /**
   * The enemy `id` noticed someone or lost them (CREATURE-AI, AI-3): an index of `ALERT_KINDS`
   * (heard: "?", saw: "!", lost). Seen by everyone near it; never says whom.
   */
  z.object({ k: z.literal('alert'), id: EntityIdSchema, s: z.number().int().min(0).max(255) }),
  /** The leader `id` shouts its group's warning before a pincer, `ms` ahead (CREATURE-AI). */
  z.object({ k: z.literal('shout'), id: EntityIdSchema, ms: z.number().int().nonnegative() }),
  /**
   * The enemy `id` surrenders for `ms` (CREATURE-AI, AI-6; JF-1): its weapon down, still; if
   * nobody finishes it, it leaves.
   */
  z.object({ k: z.literal('surrender'), id: EntityIdSchema, ms: z.number().int().nonnegative() }),
  /**
   * `id` starts using an item for `ms` (ITEM-CONSUME): `i` is the item's index in definition
   * order (the inventory's `weights`), for everyone near to see what it drinks or eats.
   */
  z.object({
    k: z.literal('using'),
    id: EntityIdSchema,
    i: z.number().int().min(0).max(65_535),
    ms: z.number().int().nonnegative(),
  }),
  /**
   * Crows take flight at `x`, `y` (fixed point, absolute; CREATURE-SPAWN, D2): a wild pack at
   * rest is near, and someone came close. A warning to draw, not an entity.
   */
  z.object({ k: z.literal('crows'), x: z.number().int(), y: z.number().int() }),
  /** `id` stopped using an item: `done` 1 if it finished, 0 if it was cut. */
  z.object({ k: z.literal('useEnd'), id: EntityIdSchema, done: z.number().int().min(0).max(1) }),
  /**
   * `id` cast its line to `x`, `y` (fixed point, absolute; RES-FISH): its float lies there until a
   * `fishEnd`.
   */
  z.object({
    k: z.literal('fishLine'),
    id: EntityIdSchema,
    x: z.number().int(),
    y: z.number().int(),
  }),
  /** Something bites `id`'s line: a fish (`c` 0) or a creature that will come out (`c` 1, PS-5). */
  z.object({ k: z.literal('bite'), id: EntityIdSchema, c: z.number().int().min(0).max(1) }),
  /** `id`'s line came out: `done` 1 with a catch, 0 empty (escaped, cut or let go). */
  z.object({ k: z.literal('fishEnd'), id: EntityIdSchema, done: z.number().int().min(0).max(1) }),
  /** A boss `id` enters its `phase` (CREATURE-BOSS: 1 the first), warned to all near. */
  z.object({ k: z.literal('phase'), id: EntityIdSchema, phase: z.number().int().min(1).max(8) }),
  /**
   * A boss `id` channels its call for `ms` (CREATURE-BOSS: the foreman strikes his anvil): when it
   * ends its crew rises; a stagger cuts it (an `interrupt`).
   */
  z.object({ k: z.literal('summon'), id: EntityIdSchema, ms: z.number().int().nonnegative() }),
  /**
   * A blow of `id` landed on the node at world tile (`x`, `y`) (RES-GATHER RG-18): `p` how far its
   * work went, 0 to 255 (done of what it needs). Everyone in sight sees the swing, the shake and the
   * chips; its striker has its own `struck`.
   */
  z.object({
    k: z.literal('nodeHit'),
    id: EntityIdSchema,
    x: z.number().int(),
    y: z.number().int(),
    p: z.number().int().min(0).max(255),
  }),
  /**
   * The node at world tile (`x`, `y`) went down under `id` (RES-GATHER RG-18: a tree falls, a rock
   * breaks): toward `a` (a byte angle, the server's, away from its feller, so every client draws the
   * same), and `n` pieces fly to `id` (at most four: some, never its yield).
   */
  z.object({
    k: z.literal('down'),
    id: EntityIdSchema,
    x: z.number().int(),
    y: z.number().int(),
    a: z.number().int().min(0).max(255),
    n: z.number().int().min(0).max(255),
  }),
]);

/** What an `alert` event says, by index (AI-3). */
export const ALERT_KINDS = ['heard', 'saw', 'lost'] as const;
export type CombatEvent = z.infer<typeof CombatEventSchema>;

/**
 * Authoritative positions at `tick`, sent at snapshotRate. Travels as a binary frame
 * (binary-snapshot.ts); this schema is the decoded shape.
 */
export const SnapshotMessageSchema = z.object({
  t: z.literal('snap'),
  tick: z.number().int().nonnegative(),
  /** Ideal simulation time of the tick, fractional at tick rates like 30 Hz. */
  time: NetTimeSchema,
  /**
   * Last movement seq the server applied from the receiver's real inputs; -1 before the first.
   * Server-side predictions of late inputs are excluded (see `me`).
   */
  ack: z.number().int().min(-1),
  /**
   * The receiver's own confirmed position at `ack`, for reconciliation. Its entry in `ents` may
   * include server predictions of inputs the receiver has not sent yet; others see that one.
   */
  me: z.tuple([FixedCoordinateSchema, FixedCoordinateSchema]),
  /** The receiver's own layer at `ack`, with `me`. */
  layer: LayerSchema,
  /**
   * Receiver's movement intents waiting in the server queue after this tick. Clients slightly
   * speed up or slow down their input ticks to keep it near one (time dilation), so client and
   * server tick rates stay matched even when their clocks drift.
   */
  queued: z.number().int().min(0).max(255),
  ents: z.array(EntityStateSchema),
  /** Combat events since the previous snapshot of the scene. */
  ev: z.array(CombatEventSchema),
  /**
   * Only while the receiver holds a helm (MOVE-SHIP T3): its ship's heading and way in 256ths (a
   * heading step's, a tile a second's), with `me` and `ack` its ship's position and inputs, so the
   * helmsman reconciles its ship as it reconciles its body.
   */
  helm: z
    .tuple([z.number().int().min(0).max(65_535), z.number().int().min(-32_768).max(32_767)])
    .optional(),
});

/**
 * A lot was claimed or what stands on it changed (HOME-CLAIM): sent to everyone subscribed to a
 * chunk it overlaps, who recompile those chunks.
 */
/**
 * A step of the own lot's dues (HOME, plan C7), to its owner: its canon falls due soon and the
 * treasury cannot cover it (`due_soon`), it is unpaid (`overdue`), its land's power noticed it was
 * raised without leave (`squat_notice`), or it fell to ruin (`ruined`). `fallsAt`: when it falls
 * to ruin if nothing changes.
 */
export const LotNoticeMessageSchema = z.object({
  t: z.literal('lotNotice'),
  lot: LotIdSchema,
  /**
   * `reclaimed`: someone else reclaimed its ruin (RC-4): it is no longer the receiver's. A small
   * site's siege (H2c, SG-4): `occupied` (its core fell, someone else controls it), `recovered`
   * (its owner holds it again) and `conquered` (it is someone else's now). `hearth_failing`: its
   * hearth, out by neglect, takes it to ruin at `fallsAt` unless kept (OP-3, ADR 0084).
   */
  step: z.enum([
    'due_soon',
    'overdue',
    'hearth_failing',
    'squat_notice',
    'ruined',
    'reclaimed',
    'occupied',
    'recovered',
    'conquered',
  ]),
  fallsAt: z.number().int().nonnegative().nullable(),
});

/**
 * What the receiver may use of others' goods by their grants (CORE-PERMISSION, T3): sent on
 * connecting and whenever a grant, its party or its company changes; never who else may. Its own
 * goods and its company's rank table are not listed: the client judges those itself.
 */
export const AccessMessageSchema = z.object({
  t: z.literal('access'),
  reach: z
    .array(
      z.object({
        good: z.string().min(1).max(64),
        kind: z.enum(['lot', 'ship']),
        uses: z.array(AccessUseSchema).min(1),
      }),
    )
    .max(200),
});

/** The band of a lawless power robbed one of the receiver's homes (LOCAL-LIFE LL-5). */
export const LotRaidedMessageSchema = z.object({
  t: z.literal('lotRaided'),
  raid: LotRaidSchema,
});

/**
 * One of the receiver's NPC workers (PROD-WORKER F3): its morale fell low (`warned`: wages or food
 * are missing), or it left (`left`: its morale reached 0).
 */
export const WorkerNoticeMessageSchema = z.object({
  t: z.literal('workerNotice'),
  worker: z.string().min(1).max(64),
  name: z.string(),
  /** `killed` or `captured` in its lot's damage window (SIEGE H2d, SG-6 a)). */
  step: z.enum(['warned', 'left', 'master', 'killed', 'captured']),
});

export const LotMessageSchema = z.object({
  t: z.literal('lot'),
  lot: LotStateSchema,
});

/**
 * A gate with a lock opened or shut (BUILD, PT-1): its piece's state alone, to the followers of its
 * lot's chunks, instead of the whole lot. `until`: open until (game time), null when not opened;
 * `closed`: it stands shut.
 */
export const GateMessageSchema = z.object({
  t: z.literal('gate'),
  lot: LotIdSchema,
  b: BuildingIdSchema,
  until: z.number().nullable(),
  closed: z.boolean(),
});

/**
 * One of the people a memory's `guide` step leads (B165, NM-2), to its character alone: by its index in
 * the step's `neighbours`, it started following (`follow`), was taken up (`carried`), stands blocked
 * by a dead one (`blocked`) or went in (`in`). The client says its line, if it has one
 * (`quests.<quest>.led.<index>.<event>`); nothing rests on it.
 */
export const LedMessageSchema = z.object({
  t: z.literal('led'),
  q: z.string().min(1).max(64),
  s: z.number().int().nonnegative(),
  i: z.number().int().nonnegative(),
  e: z.enum(['follow', 'carried', 'blocked', 'in']),
});

/** A lot is gone from the world (a temporary camp expired, folded or wrecked: plan C8). */
export const LotGoneMessageSchema = z.object({
  t: z.literal('lotGone'),
  lot: LotIdSchema,
});

/**
 * Trees planted changed (RES-GATHER RG-13: planted, grown, gone), to the followers of their
 * chunks: `set` laid on the map, `remove` (world tiles) taken off.
 */
export const PlantingsMessageSchema = z.object({
  t: z.literal('plantings'),
  set: z.array(PlantingStateSchema),
  remove: z.array(z.tuple([z.number().int(), z.number().int()])),
});

/** Resource nodes changed availability (to the subscribers of their chunks). */
export const NodesMessageSchema = z.object({
  t: z.literal('nodes'),
  changes: z.array(NodeStateSchema).min(1),
});

/** A place's chest filled or emptied (AI-4), for the followers of its chunk. */
export const ChestMessageSchema = z.object({
  t: z.literal('chest'),
  place: z.string().max(64),
  filled: z.boolean(),
});

/** Looting a chest started (AI-4): it completes after `durationMs` unless cut. */
export const ChestLootingMessageSchema = z.object({
  t: z.literal('chestLooting'),
  req: RequestRefSchema,
  durationMs: z.number().int().positive(),
});

/** Opening a treasure started (T1): it ends `durationMs` later unless cut. */
export const TreasureOpeningMessageSchema = z.object({
  t: z.literal('treasureOpening'),
  req: RequestRefSchema,
  durationMs: z.number().int().positive(),
});

/** A treasure was found (T1): the backpack now (what did not fit lies on the ground by it). */
export const TreasureOpenedMessageSchema = z.object({
  t: z.literal('treasureOpened'),
  req: RequestRefSchema,
  treasure: z.string(),
  inventory: InventorySchema,
});

/**
 * The hidden treasures near the character (T1, TS-1; OC-2: the server alone decides), each with
 * its world tile, since only the server's data holds where they lie: the whole set each time it
 * changes. Its client leaves out what it found already.
 */
export const TreasuresNearMessageSchema = z.object({
  t: z.literal('treasuresNear'),
  near: z
    .array(
      z.object({
        id: z.string(),
        x: z.number().int(),
        y: z.number().int(),
        /** A random one's lock (WORLD-TREASURE T2): how long opening it takes; absent, a fixed one. */
        lockMs: z.number().int().positive().optional(),
      }),
    )
    .max(32),
});

/**
 * The lots' traps the receiver sees (SIEGE H2e, R-63; OC-2: the server alone decides, never the
 * whole lot's): near enough for it (closer the better hidden; farther coming slowly or probing),
 * all of a lot it has rights on, and one that just sprang. In world tiles, the whole set each time
 * it changes; `armed` false while it waits to rearm.
 */
export const TrapsMessageSchema = z.object({
  t: z.literal('traps'),
  near: z
    .array(
      z.object({
        id: z.string(),
        kind: StructureDefIdSchema,
        x: z.number().int(),
        y: z.number().int(),
        width: z.number().int().positive(),
        height: z.number().int().positive(),
        armed: z.boolean(),
      }),
    )
    .max(64),
});

/**
 * The presages the receiver feels (WORLD-PRESAGE, AG-1..7), told whole when the set or a degree
 * changes: what appeared for a while near it, by its degree (1 to 3: what it asks of one of its
 * zone's level) and where, rounded to the catalog's `presage.grain`; never what it is (AG-4) nor
 * what it gives (AG-5). A random treasure comes with its `light` and its exact spot (QT-3b: its
 * column of light in view, a mark on the screen's edge).
 */
export const PresagesMessageSchema = z.object({
  t: z.literal('presages'),
  presages: z
    .array(
      z.object({
        id: z.string(),
        x: z.number().int(),
        y: z.number().int(),
        degree: z.union([z.literal(1), z.literal(2), z.literal(3)]),
        light: z.literal(true).optional(),
      }),
    )
    .max(32),
});
export type PresagesMessage = z.infer<typeof PresagesMessageSchema>;

/** A chest was looted (AI-4): what came into the backpack (objects by item id, and goods). */
export const ChestLootedMessageSchema = z.object({
  t: z.literal('chestLooted'),
  req: RequestRefSchema,
  items: z.array(ItemIdSchema),
  goods: ItemAmountsSchema,
  inventory: InventorySchema,
});

/** Using an item started (ITEM-CONSUME): it completes after `durationMs` unless cut. */
export const ConsumingMessageSchema = z.object({
  t: z.literal('consuming'),
  req: RequestRefSchema,
  item: ItemIdSchema,
  durationMs: z.number().int().positive(),
});

/** The item was used (ITEM-CONSUME): one unit left the backpack and its effects apply. */
export const ConsumedMessageSchema = z.object({
  t: z.literal('consumed'),
  req: RequestRefSchema,
  item: ItemIdSchema,
  inventory: InventorySchema,
});

/**
 * What the owner's quiver holds (ITEM-AMMO), by item: sent when entering the world and after
 * every shot. Only the owner hears it.
 */
export const AmmoMessageSchema = z.object({
  t: z.literal('ammo'),
  quiver: z.record(ItemIdSchema, z.number().int().nonnegative()),
  /** How many it holds at most, all items together (`inventory.quiverCapacity`). */
  capacity: z.number().int().positive(),
  /**
   * What the wand worn holds (ADR 0057), by charge good, and its most: absent when no charged
   * weapon is worn.
   */
  charge: z
    .object({
      items: z.record(ItemIdSchema, z.number().int().nonnegative()),
      max: z.number().int().positive(),
    })
    .optional(),
});

/** A wand's charge started (ADR 0057): it ends `durationMs` later unless a blow cuts it. */
export const LoadingMessageSchema = z.object({
  t: z.literal('loading'),
  req: RequestRefSchema,
  item: ItemIdSchema,
  durationMs: z.number().int().nonnegative(),
});

/** A piece by hand started (B064): it ends `durationMs` later unless combat cuts it. */
export const HandcraftingMessageSchema = z.object({
  t: z.literal('handcrafting'),
  req: RequestRefSchema,
  recipe: ItemIdSchema,
  durationMs: z.number().int().nonnegative(),
});

/** A piece by hand was made (B064): its goods left the backpack and the piece is in it. */
export const HandcraftedMessageSchema = z.object({
  t: z.literal('handcrafted'),
  req: RequestRefSchema,
  recipe: ItemIdSchema,
  inventory: InventorySchema,
});

/** The wand was charged (ADR 0057): the fragment left the backpack; its charge now. */
export const LoadedMessageSchema = z.object({
  t: z.literal('loaded'),
  req: RequestRefSchema,
  charge: z.record(ItemIdSchema, z.number().int().nonnegative()),
  inventory: InventorySchema,
});

/** A fill or an empty of the quiver was done (ITEM-AMMO): the quiver and the backpack now. */
export const QuiveredMessageSchema = z.object({
  t: z.literal('quivered'),
  req: RequestRefSchema,
  quiver: z.record(ItemIdSchema, z.number().int().nonnegative()),
  inventory: InventorySchema,
});

/**
 * The owner changed its weapon or other hand in combat (EQ-6): for `ms` it does not attack, guard
 * nor use a technique. Only the owner hears it.
 */
export const SwapMessageSchema = z.object({
  t: z.literal('swap'),
  ms: z.number().int().nonnegative().max(5_000),
});

/**
 * A shared cooldown of the owner's (ITEM-CONSUME, R-31): its group's items can be used again from
 * net time `until`. Only the owner hears it; sent when it starts and when entering the world.
 */
export const CooldownMessageSchema = z.object({
  t: z.literal('cooldown'),
  group: z.string().max(32),
  until: NetTimeSchema,
});

/**
 * The line landed (RES-FISH): the wait for a bite began. Moving, attacking, dodging, a technique or
 * a blow cuts it (`error` `action_interrupted` citing `req`).
 */
export const FishingMessageSchema = z.object({
  t: z.literal('fishing'),
  req: RequestRefSchema,
});

/**
 * The fishing of `req` ended: what was caught went into the backpack (`items`, `inventory`), or
 * nothing did (`items` absent: it got away, reeled too soon or too late).
 */
export const FishedMessageSchema = z.object({
  t: z.literal('fished'),
  req: RequestRefSchema,
  items: ItemAmountsSchema.optional(),
  inventory: InventorySchema.optional(),
});

/**
 * Gathering started: it completes after `durationMs` if the character stays put. `tool` is the
 * backpack's tool the server chose (RES-GATHER), absent by hand: its owner shows it in hand.
 */
export const GatheringMessageSchema = z.object({
  t: z.literal('gathering'),
  req: RequestRefSchema,
  node: NodeIdSchema,
  durationMs: z.number().int().positive(),
  tool: ItemIdSchema.optional(),
});

/**
 * A blow landed on a node (B147): `done` of the `need` it takes; the last one is followed by
 * `gathered`. Only its striker hears it.
 */
export const StruckMessageSchema = z.object({
  t: z.literal('struck'),
  req: RequestRefSchema,
  node: NodeIdSchema,
  done: z.number().int().positive(),
  need: z.number().int().positive(),
});

/**
 * A blow at a node met the air (RES-GATHER RG-20): its swing did not reach the node. It spent its
 * tool's cycle and added nothing; a gathering under way keeps its blows. Only its striker hears it.
 */
export const MissedMessageSchema = z.object({
  t: z.literal('missed'),
  req: RequestRefSchema,
  node: NodeIdSchema,
});

/** Sowing started (RG-13 R2): it completes after `durationMs` if the planter stays put. */
export const SowingMessageSchema = z.object({
  t: z.literal('sowing'),
  req: RequestRefSchema,
  x: z.number().int(),
  y: z.number().int(),
  durationMs: z.number().int().positive(),
  tool: ItemIdSchema,
});

/** Setting a gunpowder charge started (GP-1): it is set after `durationMs` if nothing cuts it. */
export const SettingMessageSchema = z.object({
  t: z.literal('setting'),
  req: RequestRefSchema,
  building: BuildingIdSchema,
  durationMs: z.number().int().positive(),
});

/** A charge set (GP-1): it left the backpack; its fuse (`fuse`) burns `fuseMs`. */
export const BlastSetMessageSchema = z.object({
  t: z.literal('blastSet'),
  req: RequestRefSchema,
  building: BuildingIdSchema,
  fuse: z.string(),
  fuseMs: z.number().int().positive(),
  inventory: InventorySchema,
});

/**
 * A fuse burning by a piece (GP-1), to whoever follows its chunk: where, against which piece, and
 * in how many milliseconds it blows unless someone puts it out (`defuse`).
 */
export const FuseMessageSchema = z.object({
  t: z.literal('fuse'),
  id: z.string(),
  building: BuildingIdSchema,
  x: z.number(),
  y: z.number(),
  endsIn: z.number().int().nonnegative(),
});

/** A fuse gone (GP-1): it blew, or someone put it out. */
export const FuseOutMessageSchema = z.object({
  t: z.literal('fuseOut'),
  id: z.string(),
  blew: z.boolean(),
});

/** Putting out a fuse started (GP-1): done after `durationMs` if nothing cuts it. */
export const DefusingMessageSchema = z.object({
  t: z.literal('defusing'),
  req: RequestRefSchema,
  durationMs: z.number().int().positive(),
});

/** A fuse put out (GP-1 a)): its charge is lost, its piece whole. */
export const DefusedMessageSchema = z.object({
  t: z.literal('defused'),
  req: RequestRefSchema,
  fuse: z.string(),
});

/** A probe started (SIEGE H2e): it completes after `durationMs` if the character stays put. */
export const ProbingMessageSchema = z.object({
  t: z.literal('probing'),
  req: RequestRefSchema,
  durationMs: z.number().int().positive(),
  tool: ItemIdSchema,
});

/** A probe completed (SIEGE H2e): the traps near now come with `traps`; the pole wore. */
export const ProbedMessageSchema = z.object({
  t: z.literal('probed'),
  req: RequestRefSchema,
  inventory: InventorySchema,
});

/** A survey started (D3): it completes after `durationMs` if the character stays put. */
export const SurveyingMessageSchema = z.object({
  t: z.literal('surveying'),
  req: RequestRefSchema,
  durationMs: z.number().int().positive(),
  tool: ItemIdSchema,
});

/** A blow at a deep vein started (DP-4): it completes after `durationMs` if the character stays. */
export const VeinWorkingMessageSchema = z.object({
  t: z.literal('veinWorking'),
  req: RequestRefSchema,
  durationMs: z.number().int().positive(),
  tool: ItemIdSchema,
});

/**
 * A blow at a deep vein completed (DP-4): what it gave, the inventory, and what is left of the vein
 * (SD-2's states), or `gone`: spent, it opens elsewhere later (GK-9).
 */
export const VeinWorkedMessageSchema = z.object({
  t: z.literal('veinWorked'),
  req: RequestRefSchema,
  items: ItemAmountsSchema,
  inventory: InventorySchema,
  reserve: z.union([ReserveStateSchema, z.literal('gone')]),
});

/**
 * A survey completed (D3): what the character knows now around where it stood, its own knowledge
 * only (OC-2), and its inventory (the kit wore).
 */
export const SurveyedMessageSchema = z.object({
  t: z.literal('surveyed'),
  req: RequestRefSchema,
  knowledge: SurveyKnowledgeSchema,
  inventory: InventorySchema,
});

/** A sapling was pulled up (PL-5): every map takes it off with a `plantings` message. */
export const UprootedMessageSchema = z.object({
  t: z.literal('uprooted'),
  req: RequestRefSchema,
  x: z.number().int(),
  y: z.number().int(),
});

/** Sown (RG-13 R2): the sapling grows into a tree at `growsAt` (game time). */
export const SownMessageSchema = z.object({
  t: z.literal('sown'),
  req: RequestRefSchema,
  x: z.number().int(),
  y: z.number().int(),
  growsAt: z.number().int().nonnegative(),
  inventory: InventorySchema,
});

/** Planting started (RG-13): it completes after `durationMs` if the planter stays put. */
export const PlantingMessageSchema = z.object({
  t: z.literal('planting'),
  req: RequestRefSchema,
  node: NodeIdSchema,
  durationMs: z.number().int().positive(),
  tool: ItemIdSchema,
});

/** Work on a project started (plan E2): it completes after `durationMs` if the worker stays. */
export const WorkingMessageSchema = z.object({
  t: z.literal('working'),
  req: RequestRefSchema,
  project: BuildingIdSchema,
  durationMs: z.number().int().positive(),
});

/**
 * Work on a project completed: its work now, and whether it is done (the structure was born under
 * the same id). Its site's followers hear the change as `town` or `lot`.
 */
export const WorkedMessageSchema = z.object({
  t: z.literal('worked'),
  req: RequestRefSchema,
  project: BuildingIdSchema,
  workDone: z.number().int().nonnegative(),
  workTotal: z.number().int().nonnegative(),
  completed: z.boolean(),
  inventory: InventorySchema,
});

/** A descent started (DEPTH-ENTRANCE): the character lands by the other end after `durationMs`. */
export const DescendingMessageSchema = z.object({
  t: z.literal('descending'),
  req: RequestRefSchema,
  durationMs: z.number().int().nonnegative(),
});

/** Felling started (RV-27): it completes after `durationMs` if the feller stays put. */
export const FellingMessageSchema = z.object({
  t: z.literal('felling'),
  req: RequestRefSchema,
  durationMs: z.number().int().positive(),
});

/** Felling completed: the wood that went into the backpack, and the resulting inventory. */
export const FelledMessageSchema = z.object({
  t: z.literal('felled'),
  req: RequestRefSchema,
  items: ItemAmountsSchema,
  inventory: InventorySchema,
});

/**
 * Forcing a stage of a lock started (plan C6, QT-4): it completes after `durationMs` if the forcer
 * stays put. `stage` (from 1) of the lock's `stages`.
 */
export const ForcingMessageSchema = z.object({
  t: z.literal('forcing'),
  req: RequestRefSchema,
  building: BuildingIdSchema,
  durationMs: z.number().int().positive(),
  stage: z.number().int().positive(),
  stages: z.number().int().positive(),
});

/**
 * A stage of the lock gave (QT-4): held in the lock a while; the last one opens the storage (its
 * lot travels as a `lot` message).
 */
export const ForcedMessageSchema = z.object({
  t: z.literal('forced'),
  req: RequestRefSchema,
  building: BuildingIdSchema,
  stage: z.number().int().positive(),
  stages: z.number().int().positive(),
});

/** Forcing a room's lock started (AR-1): it completes after `durationMs` if the forcer stays put. */
export const RoomForcingMessageSchema = z.object({
  t: z.literal('roomForcing'),
  req: RequestRefSchema,
  inn: z.string(),
  room: z.number().int().positive(),
  durationMs: z.number().int().positive(),
});

/** The room's lock gave (AR-3): it stands open to anyone by its inn's door until its owner relocks it. */
export const RoomForcedMessageSchema = z.object({
  t: z.literal('roomForced'),
  req: RequestRefSchema,
  inn: z.string(),
  room: z.number().int().positive(),
});

/** Wrecking a camp started (plan C8): it completes after `durationMs` if the wrecker stays put. */
export const WreckingMessageSchema = z.object({
  t: z.literal('wrecking'),
  req: RequestRefSchema,
  building: BuildingIdSchema,
  durationMs: z.number().int().positive(),
});

/** A stroke against a piece started (SIEGE H2a): it lands after `durationMs` if its striker stays. */
export const BreachingMessageSchema = z.object({
  t: z.literal('breaching'),
  req: RequestRefSchema,
  building: BuildingIdSchema,
  durationMs: z.number().int().positive(),
});

/**
 * A stroke landed (SIEGE H2a): the piece's integrity left, and whether it fell to ruin (its lot's
 * followers hear the rest, `lot`).
 */
export const BreachedMessageSchema = z.object({
  t: z.literal('breached'),
  req: RequestRefSchema,
  building: BuildingIdSchema,
  integrity: z.number().int().nonnegative(),
  ruined: z.boolean(),
});

/** The camp fell: its chest lies on the ground (its lot is gone, `lotGone`). */
export const WreckedMessageSchema = z.object({
  t: z.literal('wrecked'),
  req: RequestRefSchema,
  building: BuildingIdSchema,
});

/**
 * Someone is forcing a lock of yours (HOME technique 11): where, never who (an alarm rings, it
 * names nobody). Sent to the owner when the forcing starts.
 */
export const HomeAlarmMessageSchema = z.object({
  t: z.literal('homeAlarm'),
  lot: LotIdSchema,
  building: BuildingIdSchema,
});

/**
 * A watchtower of the receiver's lot saw a siege coming (SIEGE H2b): where, never who. `cargo`:
 * someone hauls a siege machine's part within its radius; `camp`: a siege camp was pitched there.
 */
export const SiegeAlarmMessageSchema = z.object({
  t: z.literal('siegeAlarm'),
  lot: LotIdSchema,
  building: BuildingIdSchema,
  seen: z.enum(['cargo', 'camp']),
});

/**
 * Many show hostility to a lot of the receiver's in its window (SIEGE GZ-6, B164): at least
 * `atLeast` of them, a band (`siege.alarm.bands`), never who nor how many exactly. Its owner's
 * company hears it too.
 */
export const RaidAlarmMessageSchema = z.object({
  t: z.literal('raidAlarm'),
  lot: LotIdSchema,
  atLeast: z.number().int().positive(),
});

/**
 * Someone is forcing the own room's lock (AR-1, like `homeAlarm`): at which inn, never who.
 */
export const RoomAlarmMessageSchema = z.object({
  t: z.literal('roomAlarm'),
  inn: z.string(),
});

/** A loot started: it completes after `durationMs` if the looter stays put and unharmed. */
export const LootingMessageSchema = z.object({
  t: z.literal('looting'),
  req: RequestRefSchema,
  durationMs: z.number().int().nonnegative(),
});

/**
 * Workers came to their posts in chunks the receiver follows, moved or went (PROD-WORKER F5): one
 * already known by its id is replaced.
 */
export const WorkersMessageSchema = z.object({
  t: z.literal('workers'),
  set: z.array(WorkerBodySchema),
  remove: z.array(z.string()),
});

/**
 * Something happens on the road ahead (WORLD-ENCOUNTER, WE-7): told only to whoever entered its
 * stretch, so it chooses before it gets there (help, loot, or pass by). `x`, `y`: where, world
 * metres.
 */
export const EncounterMessageSchema = z.object({
  t: z.literal('encounter'),
  /**
   * A caravan in trouble, a patrol, a district's raiders on a secondary road (OE-6), or a wounded
   * traveler asking to be taken somewhere (EN-3).
   */
  kind: z.enum(['caravan', 'patrol', 'ambush', 'traveler']),
  x: z.number(),
  y: z.number(),
});

/**
 * A wounded traveler's way (WORLD-ENCOUNTER EN-3, VJ-1..3), told to its escort alone: where it would
 * go when spoken to (`offer`), that it follows (`follow`), that it is falling behind (`wait`), and
 * how its way ended: `lost` (left behind or alone), `arrived` (brought: the pay comes apart),
 * `fallen`. `place`: where it goes (a place id).
 */
export const EscortServerMessageSchema = z.object({
  t: z.literal('escort'),
  k: z.enum(['offer', 'follow', 'wait', 'lost', 'arrived', 'fallen', 'told']),
  id: EntityIdSchema,
  place: z.string().max(64),
  /** `told`: what it said on parting (EN-4), a rumor heard (NR-6). */
  line: RumorLineSchema.optional(),
});

/** Objects fell or were picked up in chunks the receiver follows (ITEM-DROP). */
export const DropsMessageSchema = z.object({
  t: z.literal('drops'),
  add: z.array(DropStateSchema),
  remove: z.array(z.string()),
});

/** A pickup started: it completes after `durationMs` unless interrupted. */
export const PickingUpMessageSchema = z.object({
  t: z.literal('pickingUp'),
  req: RequestRefSchema,
  durationMs: z.number().int().nonnegative(),
});

/** A harvest started (RES-GATHER RG-4): it completes after `durationMs` unless interrupted. */
export const HarvestingMessageSchema = z.object({
  t: z.literal('harvesting'),
  req: RequestRefSchema,
  durationMs: z.number().int().nonnegative(),
});

/**
 * Who holds a traced region's ground now (LOCAL-LIFE LL-1), cell by cell (row by row from its trace's
 * origin; null: nobody's): after each of its days. Public, like banners: everyone hears it.
 */
export const GroundMessageSchema = z.object({
  t: z.literal('ground'),
  region: z.string(),
  holders: z.array(z.string().nullable()),
});

/**
 * A world event as its region hears of it (WORLD-EVENT, IE-5): its class, where (its site, world
 * tiles), its stage and its strength.
 */
export const WorldEventViewSchema = z.object({
  id: z.string(),
  class: z.string(),
  region: z.string(),
  site: z.string(),
  /** What names it (ZN-2): the zone of a point laid off the places (OE-2), else the place it is at. */
  zone: z.string().optional(),
  place: z.string().optional(),
  /** A camp's people (a creature family): the clan's camp is never told as bandits' (§37.2). */
  kin: z.string().optional(),
  x: z.number(),
  y: z.number(),
  stage: z.number().int().nonnegative(),
  strength: z.number().positive(),
  /** A rift's cycles unclosed (EV-1): each brings more. */
  cycle: z.number().int().nonnegative(),
  /** When its stage now ends, in game time (a siege's countdown, any stage's). */
  stageEndsAt: z.number().optional(),
  /**
   * An invasion's march (I4): the community it goes for, its waypoints (world metres) from its
   * rift's mouth, when it left and arrives (game time), its troop (index 0 its lieutenant) and the
   * indices of it fallen. Where it is follows from these alone (`invasionAt`).
   */
  march: z
    .object({
      target: z.string(),
      path: z.array(z.tuple([z.number(), z.number()])).min(2),
      departAt: z.number(),
      arriveAt: z.number(),
      troop: z.array(z.object({ sheet: z.string(), elite: z.boolean() })),
      fallen: z.array(z.number().int().nonnegative()),
      /**
       * The rift's reinforcements (GZ-8): its troop from `from` on, sent from its mouth on arrival
       * when more defend than its strength counted, walking the same path on their own time.
       */
      reinforcements: z
        .object({
          from: z.number().int().positive(),
          departAt: z.number(),
          arriveAt: z.number(),
        })
        .optional(),
    })
    .optional(),
});
export type WorldEventView = z.infer<typeof WorldEventViewSchema>;

/**
 * A world event announced in its region (EV-4: medium and major ones, never per blow): born, moved
 * to its next stage, or ended. Only the characters in its region hear it.
 */
export const WorldEventMessageSchema = z.object({
  t: z.literal('worldEvent'),
  event: WorldEventViewSchema,
  ended: z.boolean(),
});

/**
 * A living community fell or stood up again (LOCAL-LIFE LL-4b, EV-3): while fallen its people are
 * gone (those who give quests stay, QU-7). Public, like the ground: everyone hears it.
 */
export const CommunityMessageSchema = z.object({
  t: z.literal('community'),
  id: z.string(),
  fallen: z.boolean(),
});

/** The toll was paid (VB-1): its safe conduct lasts until `until` (net time of the snapshots). */
export const TolledMessageSchema = z.object({
  t: z.literal('tolled'),
  req: RequestRefSchema,
  until: NetTimeSchema,
});

/** A harvest completed: the parts taken went into the backpack. */
export const HarvestedMessageSchema = z.object({
  t: z.literal('harvested'),
  req: RequestRefSchema,
  parts: ItemAmountsSchema,
  inventory: InventorySchema,
});

/**
 * What changed of the flocks near the receiver (CREATURE-FLIER): each one's last scare (game time),
 * its birds fallen in this window and the window. A flock never told is whole and unscared; where
 * each bird is follows from `birdAt`.
 */
export const FlocksMessageSchema = z.object({
  t: z.literal('flocks'),
  flocks: z.array(
    z.object({
      id: z.string().max(64),
      scaredAt: z.number().nullable(),
      dead: z.array(z.number().int().nonnegative()),
      window: z.number().int().nonnegative(),
    }),
  ),
});

/**
 * What changed among the walls of ice near the receiver (MAGIC-WALL): those come near (each one's
 * tiles as world tile pairs, x, y, x, y…, and the milliseconds it stands yet) and the ids of those
 * melted or left behind. `reset` drops every wall held before (a shard's first word to it).
 */
export const WallsMessageSchema = z.object({
  t: z.literal('walls'),
  add: z.array(
    z.object({
      id: z.string().max(80),
      tiles: z.array(z.number().int()).max(48),
      ms: z.number().int().nonnegative(),
    }),
  ),
  gone: z.array(z.string().max(80)),
  reset: z.boolean(),
});

/** Something let fall (B161): the backpack as it is now. */
export const DiscardedMessageSchema = z.object({
  t: z.literal('discarded'),
  req: RequestRefSchema,
  inventory: InventorySchema,
});

/** A pickup completed: the objects are in the backpack. */
export const PickedUpMessageSchema = z.object({
  t: z.literal('pickedUp'),
  req: RequestRefSchema,
  items: z.array(ItemIdSchema),
  /** Goods picked up (DEATH-LOOT); what did not fit stays on the ground. */
  goods: ItemAmountsSchema,
  inventory: InventorySchema,
});

/** A loot completed: what went into the backpack, and the resulting inventory. */
export const LootedMessageSchema = z.object({
  t: z.literal('looted'),
  req: RequestRefSchema,
  quantity: z.number().int().nonnegative(),
  inventory: InventorySchema,
});

/**
 * A camp changed (claimed, looted, repaired). Sent to the subscribers of its chunk and to its
 * owner wherever they are, so the owner learns about a loot.
 */
export const CampMessageSchema = z.object({
  t: z.literal('camp'),
  camp: CampStateSchema,
  /** Whether its guard stands (absent in notices to a distant owner). */
  defended: z.boolean().optional(),
});

/**
 * An object of the receiver crossed into wear (ITEM-WEAR §3, RES-GATHER RG-9, B171): a tool works
 * slower from now on, a weapon, a shield or an armor piece does less, down to its floor at 0.
 * Only to its owner, once per mark crossed (a fighting piece's second at `wear.combat.lowAt`),
 * never per use.
 */
export const WearMessageSchema = z.object({
  t: z.literal('wear'),
  object: ObjectIdSchema,
  item: ItemIdSchema,
  durability: z.tuple([z.number().nonnegative(), z.number().positive()]),
});

/**
 * Gathering completed: what went into the backpack, and the resulting inventory. `spilled`, the
 * drop of what did not fit (RES-GATHER RG-18: a felled tree's overflow at its foot, the gatherer's
 * for a while).
 */
export const GatheredMessageSchema = z.object({
  t: z.literal('gathered'),
  req: RequestRefSchema,
  node: NodeIdSchema,
  items: ItemAmountsSchema,
  inventory: InventorySchema,
  spilled: z.string().max(64).optional(),
});

/**
 * The beasts' tracks its receiver sees changed (RES-GATHER CA-2): only to it, only what it sees
 * (OC-2: the server chose them). Each added one is `[id, x, y, heading, age, kind]`: metres, the
 * way the beast went (byte angle), its age in seconds when told (-1: an old trail's, which lasts
 * while its beasts live) and its kind (index in `TRACK_KINDS`).
 */
export const TracksMessageSchema = z.object({
  t: z.literal('tracks'),
  add: z.array(
    z.tuple([
      z.number().int().positive(),
      z.number(),
      z.number(),
      z.number().int().min(0).max(255),
      z.number().int().min(-1),
      z
        .number()
        .int()
        .min(0)
        .max(TRACK_KINDS.length - 1),
    ]),
  ),
  remove: z.array(z.number().int().positive()),
});

/**
 * Planting completed (RG-13): the stump now grows back by `respawnAt` (game time); the sapling
 * left the backpack.
 */
export const PlantedMessageSchema = z.object({
  t: z.literal('planted'),
  req: RequestRefSchema,
  node: NodeIdSchema,
  respawnAt: z.number().int().nonnegative(),
  inventory: InventorySchema,
});

/**
 * The market changed (an offer published, sold or cancelled): clients showing it refresh.
 * Sent to every connected player; it carries no data, the market is read over HTTP.
 */
export const MarketChangedMessageSchema = z.object({ t: z.literal('marketChanged') });

/**
 * The receiver's holdings changed through someone else's action (their offer was bought):
 * the client reads its inventory again. Carries no data.
 */
export const InventoryChangedMessageSchema = z.object({ t: z.literal('inventoryChanged') });

/**
 * The receiver's company or its invitations changed by someone else's action (B105): the
 * client reads them again. Carries no data.
 */
export const CompanyChangedMessageSchema = z.object({ t: z.literal('companyChanged') });

/**
 * Why a newcomer fell, for the hint after its death (CHAR-ORIGIN OG-4, PM-1), by priority: another
 * player, a state's pulse, a zone, a crowd, a far stronger foe, a shot, a blow with its warning.
 * A contract: the catalogs' `deathHints.<cause>`.
 */
export const DEATH_HINTS = [
  'player',
  'state',
  'zone',
  'crowd',
  'stronger',
  'ranged',
  'telegraphed',
  /** Its own nemesis felled it (B165, NM-3): told at any level while its hunt is open. */
  'nemesis',
] as const;
export const DeathHintSchema = z.enum(DEATH_HINTS);
export type DeathHint = z.infer<typeof DeathHintSchema>;

/**
 * The receiver's character died: sent before the `world` that puts it back at home.
 * `lost` is what was destroyed, `fell` what lies on the ground where it fell (objects counted
 * one each): what its death's context costs (DEATH-STATE).
 */
export const DeathMessageSchema = z.object({
  t: z.literal('death'),
  /** Who dealt the killing blow: a player's name or an enemy kind (translated by clients). */
  killer: z.object({ kind: z.enum(['player', 'enemy']), name: z.string() }).nullable(),
  lost: ItemAmountsSchema,
  fell: ItemAmountsSchema,
  /**
   * The context its death was resolved by (`combat.death.contexts`: `pve`, `pvp`, or one a rule
   * picks), for the death screen's line; absent from an older server.
   */
  context: z.string().min(1).max(32).optional(),
  /** Why it fell, while it is a newcomer (OG-4, OI-5: `novices.maxLevel` or below). */
  hint: DeathHintSchema.optional(),
});

/**
 * The receiver's character now stands in another band with a power (CHAR-REPUTATION): what one
 * of its members saw it do (RP-3) moved it there.
 */
export const ReputationMessageSchema = z.object({
  t: z.literal('reputation'),
  power: PowerIdSchema,
  band: ReputationBandSchema,
});

/**
 * A coalition of powers against the receiver's faction formed, climbed a step or ended
 * (NPC-KINGDOM-REACT, IM-2: every step told); null once none stands.
 */
export const CoalitionMessageSchema = z.object({
  t: z.literal('coalition'),
  coalition: CoalitionViewSchema.nullable(),
});

/**
 * How warm the receiver's Reliquary is (DEATH-PULSE PF-4, RH-1): by its nearness to its own
 * residue, never its way; null when it wears none. Told only when it changes, only to it (OC-2).
 */
export const ReliquaryMessageSchema = z.object({
  t: z.literal('reliquary'),
  grade: z.enum(['cold', 'warm', 'hot']).nullable(),
});

/**
 * A Stele the receiver stands by, in the Pulse (PF-4, RH-3): the way to its own residue as a byte
 * angle (0 east, growing toward +y), or null when it has none; `stele` null once it stands by none.
 * Told only when it changes, only to it.
 */
export const SteleMessageSchema = z.object({
  t: z.literal('stele'),
  stele: z.string().min(1).max(64).nullable(),
  toward: z.number().int().min(0).max(255).nullable(),
});

/** A board contract the receiver took changed (QUEST-BOARD): how it stands now. */
export const ContractMessageSchema = z.object({
  t: z.literal('contract'),
  contract: ContractViewSchema,
});

/** A chain of the receiver's quest log changed (QUEST-LOG, technical 5): its entry now. */
export const QuestMessageSchema = z.object({
  t: z.literal('quest'),
  quest: QuestEntrySchema,
  /** The campaign's givers needed after this change (KM-9), as in the log. */
  needed: NeededGiversSchema.optional(),
});

/**
 * The receiver's character learned something of the world (WORLD-DISCOVERY): regions and
 * places it now knows. The map shows only known things; this tells the client to refresh it.
 */
export const DiscoveredMessageSchema = z.object({
  t: z.literal('discovered'),
  regions: z.array(z.string().max(64)),
  places: z.array(z.string().max(64)),
});

/** Answer to `equip` and `unequip`: what is worn now, and the weapon tier it gives. */
/**
 * The receiver's pace changed (what it carries changed, MOVE-CHARACTER): it rules the inputs
 * after `from` (a seq), so prediction and the server step every input with the same pace.
 */
export const PaceMessageSchema = z.object({
  t: z.literal('pace'),
  pace: PaceSchema,
  from: z.number().int().min(-1),
  /** Whether it may swim the deep water from `from` on (MOVE-SWIM T3): what it carries decides. */
  swims: z.boolean(),
});

/**
 * The receiver's god mode changed (B131, dev tools only): from input `from` on it steps as a ghost
 * (`stepBody`'s `ghost`: nothing blocks, its layer kept) at its pace times `speed`; off, as before.
 */
export const GodMessageSchema = z.object({
  t: z.literal('god'),
  on: z.boolean(),
  speed: z.number().int().min(1).max(GOD_MAX_SPEED),
  from: z.number().int().min(-1),
  /** Its blows deal 9999 (B131); absent, as usual. */
  fullDamage: z.literal(true).optional(),
  /** Nothing costs it stamina (B131): its client predicts no spending; absent, as usual. */
  freeStamina: z.literal(true).optional(),
  /** Nothing is hidden from it (B131): walls, night, fog; absent, as usual. */
  seeAll: z.literal(true).optional(),
  /** Creatures still perceive it (B131, a scenario watched); absent, nothing does. */
  perceived: z.literal(true).optional(),
});

export const EquippedMessageSchema = z.object({
  t: z.literal('equipped'),
  req: RequestRefSchema,
  weaponLevel: z.number().int().nonnegative(),
  inventory: InventorySchema,
});

/**
 * The owner's Focus as the server settled it (MAGIC-FOCUS): charges at net time `since`, its
 * maximum and when its combat ends; the client predicts it from there with the shared rules.
 * Only the owner hears it (the rival's Focus is read from what it spends).
 */
/**
 * The receiver's Tone changed (DEATH-PULSE §4, §5): a death, a rest begun or ended, the own
 * residue reclaimed, a feat, the Dissonance's grade; between them it rises by itself (`toneAt`).
 * Only its owner hears it (OC-2).
 */
export const ToneMessageSchema = z.object({
  t: z.literal('tone'),
  tone: ToneStateSchema,
});

/**
 * The receiver's Corruption (COMBAT-STATUS-BATCH, CX-1): its charges and their most, each a share
 * of its maximum health. Told when they change, and after `world` while it has any (they live in
 * its shard, never persisted: a new session starts with none). Only its owner hears it (OC-2).
 */
export const CorruptionMessageSchema = z.object({
  t: z.literal('corruption'),
  charges: z.number().int().nonnegative(),
  max: z.number().int().positive(),
  /** The state that shows them (`corruption.state`): its HUD cell carries the count. */
  state: z.string().min(1),
});

export const FocusMessageSchema = z.object({
  t: z.literal('focus'),
  charges: z.number().int().nonnegative(),
  since: NetTimeSchema,
  max: z.number().int().positive(),
  inCombatUntil: NetTimeSchema,
  /** Its in-combat period at its level (PW-8); absent: the welcome's. */
  period: z.number().int().positive().optional(),
});

/**
 * The owner's stamina as last spent (COMBAT-DEFENSE, B049), and its armor's factors (`cost`
 * multiplies what the own actions cost, `regen` the recovery): the client predicts it from there
 * with the shared `staminaAt`. Only the owner hears it. `restMs` is when it comes back relative to
 * when the server sent this (negative: already coming back): the client counts it on its own clock
 * from when it arrives, so a client clock off the server's (a phone running seconds ahead) never
 * makes the bar refill early.
 */
export const StaminaMessageSchema = z.object({
  t: z.literal('stamina'),
  amount: z.number().nonnegative(),
  since: NetTimeSchema,
  restMs: z.number(),
  exhausted: z.boolean(),
  cost: z.number().positive(),
  regen: z.number().positive(),
  /** Its maximum (Constitution, AX-1); absent: the welcome's. */
  max: z.number().positive().optional(),
  /** × what a dodge and a guarded blow cost it (Dexterity, AX-3); absent: 1. */
  defense: z.number().positive().optional(),
});

/**
 * A pace modifier the server put on the owner's inputs (COMBAT-IMPACT, COMBAT-CC): `factor`
 * (0: stunned) multiplies the pace of inputs `from` to `until` (excluded), as a dash's does, so
 * the owner predicts them exactly.
 */
export const BoostMessageSchema = z.object({
  t: z.literal('boost'),
  from: z.number().int().nonnegative(),
  until: z.number().int().nonnegative(),
  factor: z.number().nonnegative().max(5),
  /**
   * How long it lasts from now: inputs the owner sends after that are free of it, however many
   * it was meant for (0: a correction, `until` is final).
   */
  ms: z.number().int().nonnegative(),
});

/**
 * A duel's news for one of its duelists (DEATH-DUEL), about the character `with`: the own
 * challenge stands (`sent`) or theirs does (`offer`) for `ms`, or stands no more (`withdrawn`:
 * replaced, or one of them left or reconnected); the duel started (`start`, lasting
 * at most `ms`); it ended (`won`, `lost`, or `void` when called off).
 */
/**
 * The receiver's party now (SOC-PARTY), sent to every member when it changes; null: in none.
 * `online` tells who is connected: a disconnected member stays a while, then leaves.
 */
export const PartyMessageSchema = z.object({
  t: z.literal('party'),
  party: z
    .object({
      id: z.number().int().positive(),
      leader: PlayerIdSchema,
      members: z
        .array(z.object({ playerId: PlayerIdSchema, name: z.string(), online: z.boolean() }))
        .max(8),
    })
    .nullable(),
});

/**
 * The party's members as they are now (SOC-PARTY), every second: where each stands, its health
 * and its mark on the map, seen or not. Positions in fixed point, like snapshots.
 */
export const PartyVitalsMessageSchema = z.object({
  t: z.literal('partyVitals'),
  members: z
    .array(
      z.object({
        playerId: PlayerIdSchema,
        x: FixedCoordinateSchema,
        y: FixedCoordinateSchema,
        hp: z.number().int().nonnegative(),
        maxHp: z.number().int().positive(),
        mark: z.tuple([FixedCoordinateSchema, FixedCoordinateSchema]).optional(),
      }),
    )
    .max(8),
});

/**
 * The sky changed from the dev tools (B113): the day's rules (its origin moved) and the
 * weather's (forced regions), for the client to compute both as the server does from now on.
 * Same shapes as the initial state's `day` and `weather`.
 */
export const SkyMessageSchema = z.object({
  t: z.literal('sky'),
  day: z.object({
    originMs: z.number().int().nonnegative(),
    dawnMs: z.number().int().positive(),
    dayMs: z.number().int().positive(),
    duskMs: z.number().int().positive(),
    nightMs: z.number().int().positive(),
  }),
  forced: z.record(z.string(), z.string()),
  /** Regions whose Pulse phase is forced (plan H1). */
  pulse: z.record(z.string(), z.enum(['calm', 'agitation', 'intense'])).default({}),
  /** Regions whose Major Pulse is forced (H2c): it rules their whole cycle. */
  major: z.array(z.string()).default([]),
  /**
   * The Intense Pulses brought forward (27.6, PA-1..3), by region and cycle: the game time each
   * was born at. The client computes the Pulse with them like the server.
   */
  advances: z.record(z.string(), z.record(z.string(), z.number().int())).default({}),
  /**
   * The game time now (B145): the dev clock jumped, and the day, the weather and the Pulse the
   * client computes go on from it. Absent when only the rules changed.
   */
  gameTime: z.number().int().nonnegative().optional(),
});

/** Someone invites the receiver to its party (it answers with `party` accept or decline). */
export const PartyInviteMessageSchema = z.object({
  t: z.literal('partyInvite'),
  from: PlayerIdSchema,
  name: z.string(),
  /** How long the invitation stands, in milliseconds. */
  ms: z.number().int().nonnegative(),
});

export const DuelMessageSchema = z.object({
  t: z.literal('duel'),
  with: EntityIdSchema,
  s: z.enum(['sent', 'offer', 'withdrawn', 'start', 'won', 'lost', 'void']),
  ms: z.number().int().nonnegative(),
});

/**
 * The server put down what the owner held (a guard broken by impact, an aim lost to a stun,
 * COMBAT-IMPACT): its slowing ends at input `from`, as the server applies it.
 */
export const LoweredMessageSchema = z.object({
  t: z.literal('lowered'),
  from: z.number().int().nonnegative(),
});

/** The answer to `attune`: the station's view, and the sheet when attributes changed. */
export const AttunedMessageSchema = z.object({
  t: z.literal('attuned'),
  req: RequestRefSchema,
  fragments: FragmentsViewSchema,
  progress: ProgressSchema.nullable(),
});

/** The answer to `learn`: the spell learned, and the Grimoire with it. */
export const LearnedMessageSchema = z.object({
  t: z.literal('learned'),
  req: RequestRefSchema,
  spell: z.string(),
  grimoire: GrimoireViewSchema,
});

/** The answer to `allocate`: the sheet with the points in place. */
export const AllocatedMessageSchema = z.object({
  t: z.literal('allocated'),
  req: RequestRefSchema,
  progress: ProgressSchema,
});

/**
 * How the owner's weapon family fights at its mastery's rank (PO-8, BR-5): the catalog's view of it
 * and of the attacks it strikes with, its traits applied, for the owner's client to predict as the
 * server judges. Told when the owner enters, changes weapon or reaches a rank; the catalog's own
 * numbers otherwise (no trait reached).
 */
export const ArmsMessageSchema = z.object({
  t: z.literal('arms'),
  family: WeaponFamilyIdSchema,
  view: FamilyViewSchema,
  attacks: z.array(z.tuple([z.number().int().nonnegative(), AttackViewSchema])).max(16),
  /** The pace of its shield's guard raised when its perks change it (A4, Muro Imparable). */
  guardPace: z.number().positive().max(1).optional(),
});

/**
 * The owner's level, experience or attributes changed (CHAR-LEVEL): after experience was earned
 * (`gained`), or attributes reassigned elsewhere.
 */
/**
 * One line of the chat as its receiver hears it (SOC-CHAT): `from` is missing for «a voice» (a
 * nearby speaker it does not see, CH-3), `ent` only when it sees the speaker; `to` is a whisper's
 * target, on the writer's own copy. Never an account, a position or a level.
 */
export const ChatLineSchema = z.object({
  id: z.number().int().nonnegative(),
  ch: ChatChannelSchema,
  /** When it was said (wall time, epoch ms). */
  at: z.number().int().nonnegative(),
  text: z.string(),
  from: z
    .object({ pid: PlayerIdSchema, name: z.string(), ent: EntityIdSchema.optional() })
    .optional(),
  to: z.object({ pid: PlayerIdSchema, name: z.string() }).optional(),
  lang: ChatLanguageSchema,
});
export type ChatLine = z.infer<typeof ChatLineSchema>;

export const ChatMessageSchema = ChatLineSchema.extend({ t: z.literal('chat') });

/** What the receiver's channels said lately, sent once as it enters (no `near`: unheard). */
export const ChatHistoryMessageSchema = z.object({
  t: z.literal('chatHistory'),
  lines: z.array(ChatLineSchema).max(1_000),
});

/** The characters the receiver ignores (SOC-BLOCK), as it enters and when it changes. */
export const ChatIgnoresMessageSchema = z.object({
  t: z.literal('chatIgnores'),
  ignored: z.array(z.object({ pid: PlayerIdSchema, name: z.string() })).max(1_000),
});

export const ProgressMessageSchema = z.object({
  t: z.literal('progress'),
  progress: ProgressSchema,
  gained: z.number().int().nonnegative(),
});

export const ServerMessageSchema = z.discriminatedUnion('t', [
  WelcomeMessageSchema,
  ChatMessageSchema,
  ChatHistoryMessageSchema,
  ChatIgnoresMessageSchema,
  WorkerNoticeMessageSchema,
  DuelMessageSchema,
  PartyMessageSchema,
  PartyInviteMessageSchema,
  SkyMessageSchema,
  PartyVitalsMessageSchema,
  PongMessageSchema,
  ErrorMessageSchema,
  WorldMessageSchema,
  ChunksMessageSchema,
  InterestMessageSchema,
  SnapshotMessageSchema,
  LotMessageSchema,
  GateMessageSchema,
  LedMessageSchema,
  LotGoneMessageSchema,
  LotNoticeMessageSchema,
  AccessMessageSchema,
  LotRaidedMessageSchema,
  NodesMessageSchema,
  GatheringMessageSchema,
  StruckMessageSchema,
  MissedMessageSchema,
  PlantingMessageSchema,
  SowingMessageSchema,
  SownMessageSchema,
  UprootedMessageSchema,
  SurveyingMessageSchema,
  ProbingMessageSchema,
  ProbedMessageSchema,
  SettingMessageSchema,
  BlastSetMessageSchema,
  FuseMessageSchema,
  FuseOutMessageSchema,
  DefusingMessageSchema,
  DefusedMessageSchema,
  SurveyedMessageSchema,
  VeinWorkingMessageSchema,
  VeinWorkedMessageSchema,
  ConsumingMessageSchema,
  ChestMessageSchema,
  ChestLootingMessageSchema,
  ChestLootedMessageSchema,
  TreasureOpeningMessageSchema,
  TreasureOpenedMessageSchema,
  TreasuresNearMessageSchema,
  PresagesMessageSchema,
  TrapsMessageSchema,
  ConsumedMessageSchema,
  AmmoMessageSchema,
  QuiveredMessageSchema,
  LoadingMessageSchema,
  LoadedMessageSchema,
  HandcraftingMessageSchema,
  HandcraftedMessageSchema,
  CooldownMessageSchema,
  SwapMessageSchema,
  GatheredMessageSchema,
  WearMessageSchema,
  PlantedMessageSchema,
  TracksMessageSchema,
  PlantingsMessageSchema,
  WorkingMessageSchema,
  WorkedMessageSchema,
  FellingMessageSchema,
  DescendingMessageSchema,
  FelledMessageSchema,
  ForcingMessageSchema,
  ForcedMessageSchema,
  RoomForcingMessageSchema,
  RoomForcedMessageSchema,
  WreckingMessageSchema,
  WreckedMessageSchema,
  BreachingMessageSchema,
  BreachedMessageSchema,
  HomeAlarmMessageSchema,
  SiegeAlarmMessageSchema,
  RaidAlarmMessageSchema,
  RoomAlarmMessageSchema,
  FishingMessageSchema,
  FishedMessageSchema,
  MarketChangedMessageSchema,
  InventoryChangedMessageSchema,
  CompanyChangedMessageSchema,
  DeathMessageSchema,
  EquippedMessageSchema,
  AllocatedMessageSchema,
  ProgressMessageSchema,
  ArmsMessageSchema,
  FocusMessageSchema,
  ToneMessageSchema,
  CorruptionMessageSchema,
  StaminaMessageSchema,
  BoostMessageSchema,
  LoweredMessageSchema,
  AttunedMessageSchema,
  LearnedMessageSchema,
  PaceMessageSchema,
  GodMessageSchema,
  DiscoveredMessageSchema,
  ReputationMessageSchema,
  CoalitionMessageSchema,
  ReliquaryMessageSchema,
  SteleMessageSchema,
  QuestMessageSchema,
  ContractMessageSchema,
  LootingMessageSchema,
  LootedMessageSchema,
  CampMessageSchema,
  DropsMessageSchema,
  WorkersMessageSchema,
  EncounterMessageSchema,
  EscortServerMessageSchema,
  PickingUpMessageSchema,
  PickedUpMessageSchema,
  DiscardedMessageSchema,
  FlocksMessageSchema,
  WallsMessageSchema,
  HarvestingMessageSchema,
  HarvestedMessageSchema,
  TolledMessageSchema,
  GroundMessageSchema,
  CommunityMessageSchema,
  WorldEventMessageSchema,
]);
export type ServerMessage = z.infer<typeof ServerMessageSchema>;

export type ClientMessageOf<T extends ClientMessage['t']> = Extract<ClientMessage, { t: T }>;
export type ServerMessageOf<T extends ServerMessage['t']> = Extract<ServerMessage, { t: T }>;
